import {MiniEvent, errorToString} from "@cdx/common";
import {
  modelReadScope,
  type MemberAddonRole,
  type Scope,
  type ScopeGroup,
  type TokenRoleName,
} from "@cdx/shared/auth/scopes";
import type {ActionKey} from "@cdx/shared/auth/action-scopes";
import {toMs} from "@cdx/shared/duration";
import descriptions from "virtual:client-models";
import type {AccountId} from "../cdx-models/Account";
import type {ActiveTimeTrackerId} from "../cdx-models/ActiveTimeTracker";
import type {AffiliateCodeId} from "../cdx-models/AffiliateCode";
import type {ApiTokenId} from "../cdx-models/ApiToken";
import type {AttachmentId} from "../cdx-models/Attachment";
import type {FileId} from "../cdx-models/File";
import type {CardId} from "../cdx-models/Card";
import type {CardPresetId} from "../cdx-models/CardPreset";
import type {CardUpvoteId} from "../cdx-models/CardUpvote";
import type {DeckId} from "../cdx-models/Deck";
import type {DiscordGuildId} from "../cdx-models/DiscordGuild";
import type {DiscordProjectNotificationId} from "../cdx-models/DiscordProjectNotification";
import type {DiscordSlashCommandId} from "../cdx-models/DiscordSlashCommand";
import type {ForecastOverwriteId} from "../cdx-models/ForecastOverwrite";
import type {IntegrationId} from "../cdx-models/Integration";
import type {JiraProjectSyncId} from "../cdx-models/JiraProjectSync";
import type {JiraSiteId} from "../cdx-models/JiraSite";
import type {MilestoneId} from "../cdx-models/Milestone";
import type {OauthGrantId} from "../cdx-models/OauthGrant";
import type {PasskeyId} from "../cdx-models/Passkey";
import type {ProjectId} from "../cdx-models/Project";
import type {ProjectSelectionId} from "../cdx-models/ProjectSelection";
import type {ProjectTagId} from "../cdx-models/ProjectTag";
import type {QueueEntryId} from "../cdx-models/QueueEntry";
import type {ResolvableId} from "../cdx-models/Resolvable";
import type {ResolvableEntryId} from "../cdx-models/ResolvableEntry";
import type {ReleaseId} from "../cdx-models/Release";
import type {ResolvableEntryReactionId} from "../cdx-models/ResolvableEntryReaction";
import type {SavedSearchId} from "../cdx-models/SavedSearch";
import type {SprintId} from "../cdx-models/Sprint";
import type {SprintConfigId} from "../cdx-models/SprintConfig";
import type {TimeTrackingSegmentId} from "../cdx-models/TimeTrackingSegment";
import type {CardSubscriptionId} from "../cdx-models/CardSubscription";
import type {DeckSubscriptionId} from "../cdx-models/DeckSubscription";
import type {UserEmailId} from "../cdx-models/UserEmail";
import type {UserId} from "../cdx-models/User";
import type {UserInvitationId} from "../cdx-models/UserInvitation";
import type {UserInviteCodeId} from "../cdx-models/UserInviteCode";
import type {UserReportSettingId} from "../cdx-models/UserReportSetting";
import type {UserTagId} from "../cdx-models/UserTag";
import type {VisionBoardId} from "../cdx-models/VisionBoard";
import type {VisionBoardQueryId} from "../cdx-models/VisionBoardQuery";
import type {WizardId} from "../cdx-models/Wizard";
import type {WorkflowItemId} from "../cdx-models/WorkflowItem";
import type {WorkflowTemplateId} from "../cdx-models/WorkflowTemplate";
import type {WorkflowTemplateItemId} from "../cdx-models/WorkflowTemplateItem";
import type {FileMeta, ProjectSpaceId} from "../cdx-models/utils/extended-fields";
import type {Workdays} from "../cdx-models/field-definitions";
import type {CardOrder} from "../features/card-order/useCardOrder";
import type {ForecastOverwritePayload} from "../features/forecast/forecast-types";
import type {SearchFilter} from "../features/search/search-categories";
import type {ProjectTemplate} from "../features/templates/template-types";
import type {
  SearchQuery as VisionBoardSearchQuery,
  VisionBoardQueryPayload,
} from "../features/vision-board/vision-board-types";
import type {
  UserReportDeckMapping,
  UserReportPrioMapping,
} from "../pages/account-settings/integrations/userReports/types";
import type {
  CreateDiscordSlashCommandParams,
  DiscordChannelPermissionAction,
  DiscordNotificationType,
  UpdateDiscordSlashCommandParams,
} from "../pages/account-settings/integrations/discord/discord-types";
import type {GithubRepo} from "../pages/account-settings/integrations/github/types";
import type {ScmWebhookProvider} from "../pages/account-settings/integrations/generic-scm/types";
import type {JiraSyncOptions} from "../pages/account-settings/integrations/jira/types";
import type {SlackProjectChannelInfo} from "../pages/account-settings/integrations/slack/types";
import createApi from "./mate";
import {getIdAsKey, INVALIDATE} from "./mate/mate-shared";
import type {Tx} from "./mate/mate-types";
import {forgetAccountScopes, peekAccountScopes} from "./auth/scopes";
import {describeMissingScope, parseScopeRefusal} from "./auth/scope-refusal";
import messenger from "./messenger";
import {MutationRefusal} from "./mutation-refusal";
import {postRequest} from "./request";
import {dateStrToDay} from "./date-utils";
import {API_ERRORS, apiErrorEvent} from "./request-utils";
import {
  handUpdates,
  placeInHandOptimistically,
  removeFromHandsOptimistically,
} from "./api-utils/hand-updates";
import {singleCardUpdate} from "./api-utils/single-card-update";
// See ./api-utils/README.md before adding/editing `defineMutation<...>()` entries —
// it explains how to derive Params/RetVal from the backend action schema.
import {
  defineMutation,
  implCreate,
  implDelete,
  implUpdate,
  type CoverFileData,
  type ImplicitData,
  type ImplicitUpdate,
  type Mutate,
  type MutationDef,
} from "./api-utils/types";
import {isSteamy} from "./app-mode";
import {fieldDependents} from "./field-dependents";

export const uuidEvent = new MiniEvent<string>();
export const mutationEvent = new MiniEvent<{action: string; data: unknown; result: unknown}>();

if (isSteamy) {
  throw new Error("Nope, you don't wanna load this for steamy!");
}

let currentSessionId: string | null = null;
uuidEvent.addListener((uuid: string) => {
  currentSessionId = uuid;
});

const fetcher = (query: unknown, sendResults: (err: unknown, result?: unknown) => void) => {
  postRequest("", {query} as any).then(
    (result) => {
      apiErrorEvent.emit(null);
      sendResults(null, result);
    },
    (e) => {
      apiErrorEvent.emit(e);
      sendResults(true);
    }
  );
};

const sensitiveKeys = new Set(["password", "oldPassword", "token", "code"]);
const strippedData = (data: Record<string, unknown>) => {
  return Object.entries(data).reduce<Record<string, unknown>>((m, [key, val]) => {
    m[key] = sensitiveKeys.has(key) ? "*****" : val;
    return m;
  }, {});
};

/** 401s and 403s the calling component answers itself, instead of a redirect to /login or /forbidden. */
const CALLER_HANDLED_REFUSALS = new Set([
  "reverification_required",
  "session_required",
  "second_factor_required",
  "no_session_awaiting_second_factor",
]);

const dispatcher = (
  actionName: string,
  data: Record<string, unknown>,
  sendResult: (err: unknown, result?: unknown) => void
) => {
  // eslint-disable-next-line no-console
  console.log("dispatching", actionName, strippedData(data));
  const cleanData = Object.entries(data).reduce<Record<string, unknown>>(
    (m, [key, val]) => {
      m[key] = typeof val === "string" ? val.trim() : val;
      return m;
    },
    {sessionId: currentSessionId}
  );
  postRequest(`/dispatch/${actionName}` as any, cleanData as any).then(
    (body: any) => {
      const result = body.payload || {};
      mutationEvent.emit({action: actionName, data, result: result});
      sendResult(null, result);
    },
    (e) => {
      if (e.type === API_ERRORS.API_ERROR) {
        const missingScope = e.status === 403 ? parseScopeRefusal(e.message) : null;
        if (e.status === 400) {
          return sendResult(new MutationRefusal(e.message.payload?.error));
        } else if (missingScope) {
          // One refused operation, not a refused page: no cache reset and no redirect to /forbidden.
          const message = describeMissingScope(missingScope);
          messenger.send(message, {type: "error"});
          return sendResult(new MutationRefusal(message, {shown: true}));
        } else if (
          (e.status === 401 || e.status === 403) &&
          CALLER_HANDLED_REFUSALS.has(e.message)
        ) {
          return sendResult(new MutationRefusal(e.message));
        } else if (e.status === 401 || e.status === 403) {
          return apiErrorEvent.emit(e);
        }
      }
      sendResult(new MutationRefusal(errorToString(e)));
    }
  );
};

/**
 * The server gives a card without an order row one, so it joins the zone right away. Rows that exist
 * take the label through the implicit update; their new sort value is only known once saved.
 */
const addMissingCardOrders = (
  tx: Tx,
  {cardIds, context, label}: {cardIds: CardId[]; context: string; label: string | null}
) => {
  const accountId = tx.get("_root", "_root", "account");
  for (const cardId of cardIds) {
    const id = getIdAsKey([context, cardId]);
    if (tx.get("cardOrder", id, "label", undefined) !== undefined) continue;
    tx.create("cardOrder", {context, cardId, label, account: accountId});
  }
};

type ZoneRename = {label: string | null; newLabel: string};

/**
 * The zone list before the rename and what's in the zone, so a rename shows at once. A push from
 * another client lacks them, so it reloads the labels instead.
 */
type ZoneRenameSupport<ContentId> = {zoneLabels: (string | null)[]; contentIds: ContentId[]};

const renamedZoneLabels = ({
  zoneLabels,
  label,
  newLabel,
}: ZoneRename & Partial<ZoneRenameSupport<unknown>>) =>
  zoneLabels
    ? (zoneLabels.length ? zoneLabels : [null]).map((l) => (l === label ? newLabel : l))
    : INVALIDATE;

const cardZoneRenameUpdates = (
  context: "deck" | "milestone" | "sprint",
  {newLabel, contentIds}: ZoneRename & Partial<ZoneRenameSupport<CardId>>
) =>
  contentIds
    ? contentIds.map((cardId) => implUpdate("cardOrder", {cardId, context, label: newLabel}))
    : [implUpdate("cardOrder", {label: INVALIDATE})];

/** `startSession`'s answer for a user with a second factor. */
type SecondFactorRequired = {
  secondFactorRequired: true;
  name: string | null;
  emailHint: string;
  secondFactors: ("passkey" | "totp")[];
};

/**
 * Keys the server broadcasts over the socket and the client never dispatches. The entry below says
 * only which cached rows to drop, so no dispatch action answers them and they carry no scope row.
 */
type ServerPushKey =
  | "decks.updateStats"
  | "milestones.updateStats"
  | "sprints.updateStats"
  | "milestoneProgress.update"
  | "sprintProgress.update"
  | "sprintConfigProgress.update"
  | "files.update"
  | "releases.create"
  | "billing.serverUpdate"
  | "billing.addInvoice";

/** What changes with an authenticator app: set up, removed or used. */
const SELF_SECURITY_FIELDS = {
  hasSecondFactor: INVALIDATE,
  unusedRecoveryCodeCount: INVALIDATE,
  totpSetUpAt: INVALIDATE,
  totpLastUsedAt: INVALIDATE,
} as const;

const mutations = {
  "cards.create": defineMutation<
    {
      content: string;
      deckId: DeckId | null;
      priority?: "a" | "b" | "c" | null;
      effort?: number | null;
      assigneeId?: UserId | null;
      masterTags?: string[];
      milestoneId?: MilestoneId | null;
      sprintId?: SprintId | null;
      parentCardId?: CardId | null;
      dueDate?: string | null;
      isDoc?: boolean;
      attachments?: {id: string; content: string; fileData: CoverFileData}[];
      fakeCoverFileId?: string | null;
      addAsBookmark?: boolean;
      putInQueue?: boolean;
      subscribeCreator?: boolean;
      orderInfo?: {
        label: string | null;
        context: "deck" | "milestone" | "sprint";
      } | null;
    },
    {id: CardId; accountSeq: number},
    {userId: UserId}
  >({
    type: "create",
    model: "card",
    convertDataForOptimistic: (data) => ({
      // convert "2016-11-23" to {year: 2016, month: 11, day: 23}
      ...data,
      ...(data.dueDate ? {dueDate: dateStrToDay(data.dueDate)} : {}),
    }),
    implicit: ({
      attachments,
      deckId,
      milestoneId,
      userId,
      parentCardId,
      assigneeId,
      addAsBookmark,
      putInQueue,
      orderInfo,
    }) => [
      implCreate("activity", {deckId, milestoneId}),
      implCreate("userTag"),
      ...(!deckId || addAsBookmark ? [implCreate("handCard", {userId})] : []),
      ...(assigneeId || putInQueue ? [...handUpdates, implCreate("assigneeAssignment")] : []),
      ...(attachments && attachments.length
        ? [implUpdate("account", {totalMediaByteUsage: INVALIDATE})]
        : []),
      ...(orderInfo ? [implCreate("cardOrder")] : []),
      ...(parentCardId
        ? [
            implUpdate("card", {
              id: parentCardId,
              status: INVALIDATE,
              effort: INVALIDATE,
              isDoc: INVALIDATE,
              childCards: INVALIDATE,
              derivedStatus: INVALIDATE,
            }),
          ]
        : []),
      ...(deckId
        ? [
            implCreate("deckAssignment", {deckId}),
            implUpdate("deckAssignment", {deckId, lastAssignedAt: INVALIDATE}),
          ]
        : []),
    ],
  }),
  "cards.createMany": defineMutation<
    {
      cards: {
        content: string;
        priority?: "a" | "b" | "c" | null;
        effort?: number | null;
        assigneeId?: UserId | null;
        isDoc?: boolean;
        visibility?: "archived" | "default" | "deleted";
        status?: "not_started" | "started" | "done" | null;
      }[];
      deckId: DeckId;
      parentCardId?: CardId | null;
    },
    {cards: {id: CardId; accountSeq: number}[]}
  >({
    type: "create",
    model: "card",
    implicit: ({deckId, parentCardId}) => [
      implCreate("activity", {deckId}),
      ...(parentCardId
        ? [
            implUpdate("card", {
              id: parentCardId,
              childCards: INVALIDATE,
              status: INVALIDATE,
              effort: INVALIDATE,
              derivedStatus: INVALIDATE,
            }),
          ]
        : []),
      implCreate("deckAssignment", {deckId}),
      implUpdate("deckAssignment", {deckId, lastAssignedAt: INVALIDATE}),
    ],
  }),
  "cards.bulkImport": defineMutation<
    {
      cards: {
        content: string;
        priority?: "a" | "b" | "c" | null;
        effort?: number | null;
        assigneeId?: UserId | null;
        isDoc?: boolean;
        visibility?: "archived" | "default" | "deleted";
        status?: "not_started" | "started" | "done" | null;
        masterTags?: string[];
      }[];
      deckId: DeckId;
    },
    void
  >({
    type: "create",
    model: "card",
    // one call makes many rows, and the default step would show a single row with only `deckId`
    optimistic: () => {},
    implicit: () => [implCreate("activity")],
  }),
  /** @deprecated use `cards.bulkImport` instead */
  "cards.bulkCreate": defineMutation<
    {
      cards: {
        content: string;
        priority?: "a" | "b" | "c" | null;
        effort?: number | null;
        assigneeId?: UserId | null;
        isDoc?: boolean;
        visibility?: "archived" | "default" | "deleted";
        status?: "not_started" | "started" | "done" | null;
        masterTags?: string[];
      }[];
      deckId: DeckId;
    },
    void
  >({
    type: "create",
    model: "card",
    // one call makes many rows, and the default step would show a single row with only `deckId`
    optimistic: () => {},
    implicit: () => [implCreate("activity")],
  }),
  "cards.update": defineMutation<
    {
      id: CardId;
      content?: string;
      priority?: "a" | "b" | "c" | null;
      effort?: number | null;
      status?: "not_started" | "started" | "done";
      assigneeId?: UserId | null;
      attachments?: AttachmentId[];
      deckId?: DeckId;
      masterTags?: string[];
      visibility?: "default" | "archived" | "deleted";
      milestoneId?: MilestoneId | null;
      sprintId?: SprintId | null;
      embeds?: unknown[];
      coverFileId?: FileId | null;
      coverFileModifier?: {
        mode: "dark" | "light";
        noBlur: boolean;
        offsetX: number;
        offsetY: number;
        bigCardOffsetX?: number;
        bigCardOffsetY?: number;
      } | null;
      parentCardId?: CardId | null;
      childCards?: CardId[] | null;
      inDeps?: CardId[];
      outDeps?: CardId[];
      dueDate?: string | null;
      isDoc?: boolean;
    },
    void
  >({
    type: "update",
    model: "card",
    convertDataForOptimistic: (data) => ({
      // convert "2016-11-23" to {year: 2016, month: 11, day: 23}
      ...data,
      ...(data.dueDate ? {dueDate: dateStrToDay(data.dueDate)} : {}),
    }),
    implicit: singleCardUpdate,
  }),
  "cards.bulkUpdate": defineMutation<
    {
      ids: CardId[];
      priority?: "a" | "b" | "c" | null;
      effort?: number | null;
      status?: "not_started" | "started" | "done";
      assigneeId?: UserId | null;
      deckId?: DeckId;
      visibility?: "default" | "archived" | "deleted";
      milestoneId?: MilestoneId | null;
      sprintId?: SprintId | null;
      dueDate?: string | null;
      isDoc?: boolean;
      addMasterTag?: string;
      removeMasterTag?: string;
      parentCardId?: CardId | null;
      deleteFiles?: boolean;
    },
    void
  >({
    type: "update",
    model: "card",
    implicit: ({ids, ...rest}, retVal) =>
      (ids || []).reduce<ImplicitUpdate[]>((l, id) => {
        l.push(...singleCardUpdate({id, ...rest}, retVal));
        return l;
      }, []),
  }),
  "cards.updateMany": defineMutation<
    {
      cards: {
        id: CardId;
        content?: string | null;
        title?: string | null;
        priority?: "a" | "b" | "c" | null;
        effort?: number | null;
        assigneeId?: UserId | null;
        isDoc?: boolean;
        visibility?: "archived" | "default" | "deleted";
        status?: "not_started" | "started" | "done" | null;
        masterTags?: string[];
      }[];
    },
    void
  >({
    type: "update",
    model: "card",
    implicit: ({cards}, retVal) =>
      (cards || []).reduce<ImplicitUpdate[]>((l, card) => {
        l.push(implUpdate("card", card), ...singleCardUpdate(card, retVal));
        return l;
      }, []),
  }),
  "cards.copy": defineMutation<
    {
      ids: CardId[];
      props: (
        | "effort"
        | "priority"
        | "assigneeId"
        | "masterTags"
        | "attachments"
        | "sprintId"
        | "milestoneId"
      )[];
      target: "hand" | DeckId;
    },
    void,
    {userId?: UserId}
  >({
    type: "create",
    model: "card",
    implicit: ({props, target, userId}) => [
      // the copy takes the source's values, which the client doesn't know here
      implCreate(
        "card",
        Object.fromEntries(props.map((p) => [p, INVALIDATE])) as ImplicitData<"card">
      ),
      implCreate("activity"),
      implCreate("card", {deckId: target === "hand" ? INVALIDATE : target}),
      ...(target === "hand" ? [implCreate("handCard", {userId})] : []),
      ...(props.includes("attachments")
        ? [implUpdate("account", {totalMediaByteUsage: INVALIDATE})]
        : []),
    ],
  }),

  "cards.addFile": defineMutation<
    {cardId: CardId; fileData: CoverFileData},
    {id: AttachmentId; url: string; meta: FileMeta}
  >({
    type: "create",
    model: "attachment",
    implicit: ({cardId}) => [
      implUpdate("card", {attachments: INVALIDATE, cardId, coverFileId: INVALIDATE}),
      implCreate("activity", {cardId}),
      implCreate("cardHistory", {cardId}),
      implUpdate("account", {totalMediaByteUsage: INVALIDATE}),
    ],
  }),
  "cards.seen": defineMutation<{cardId: CardId}, void>({
    type: "void",
    implicit: ({cardId}) => [implUpdate("card", {cardId, meta: INVALIDATE})],
  }),

  "files.create": defineMutation<{fileData: CoverFileData}, {id: FileId}>({
    type: "create",
    model: "file",
  }),

  "decks.create": defineMutation<
    {
      title: string;
      projectId: ProjectId;
      coverFileData?: CoverFileData | null;
      coverColor?: string | null;
      spaceId?: number;
      deckType?: "task" | "hero" | "doc" | "mixed";
    },
    {id: DeckId},
    {userId: UserId}
  >({
    type: "create",
    model: "deck",
    implicit: ({userId}, opts) => [
      ...(opts._isOptimistic ? [] : [implCreate("deckSubscription", {userId, deckId: opts.id})]),
      implCreate("activity"),
    ],
  }),
  "decks.update": defineMutation<
    {
      id: DeckId;
      milestoneId?: MilestoneId | null;
      title?: string;
      description?: string;
      handSyncEnabled?: boolean;
      defaultProjectTagId?: ProjectTagId | null;
      stickyDefaultProjectTag?: boolean;
      preferredOrder?: CardOrder | null;
      coverFileData?: CoverFileData | null;
      descriptionCoverFileId?: FileId | null;
      defaultCard?: {
        content?: string;
        assigneeId?: UserId | null;
        priority?: "a" | "b" | "c" | null;
        effort?: number | null;
        deckId?: DeckId | null;
        masterTags?: string[];
      };
      manualOrderLabels?: (string | null)[];
      workflowItemOrderLabels?: (string | null)[];
      coverColor?: string | null;
      deckType?: "task" | "hero" | "doc" | "mixed";
    },
    void
  >({
    type: "update",
    model: "deck",
    implicit: ({
      id,
      milestoneId,
      coverFileData,
      handSyncEnabled,
      defaultProjectTagId,
      deckType,
    }) => [
      ...(coverFileData ? [implUpdate("deck", {coverFileId: INVALIDATE, id})] : []),
      ...(milestoneId !== undefined ? [implUpdate("card", {milestoneId})] : []),
      ...(handSyncEnabled ? handUpdates : []),
      ...(deckType ? [implUpdate("card", {derivedStatus: INVALIDATE, status: INVALIDATE})] : []),
      ...(defaultProjectTagId || defaultProjectTagId === null
        ? [
            implUpdate("card", {
              masterTags: INVALIDATE,
              content: INVALIDATE,
              title: INVALIDATE,
            }),
          ]
        : []),
    ],
  }),
  "decks.renameZone": defineMutation<{id: DeckId} & ZoneRename, void, ZoneRenameSupport<CardId>>({
    type: "void",
    implicit: (data) => [
      implUpdate("deck", {id: data.id, manualOrderLabels: renamedZoneLabels(data)}),
      ...cardZoneRenameUpdates("deck", data),
    ],
  }),
  "decks.updateCover": defineMutation<{id: DeckId; coverFileData: CoverFileData | null}, void>({
    type: "update",
    model: "deck",
    implicit: ({id}) => [implUpdate("deck", {coverFileId: INVALIDATE, id})],
  }),
  "decks.delete": defineMutation<{id: DeckId}, void>({
    type: "update",
    model: "deck",
    implicit: ({id}) => [
      implUpdate("deck", {isDeleted: true, id}),
      implUpdate("card", {visibility: INVALIDATE}),
    ],
  }),
  // called by the server only
  "decks.updateStats": {
    type: "void",
    implicit: ({id}: any) => [implUpdate("deck", {id, stats: INVALIDATE})],
  },

  "decks.updateGuardians": defineMutation<{id: DeckId; userIds: UserId[]}, void>({
    type: "void",
    implicit: ({id, userIds}) => [
      implUpdate("deck", {id, hasGuardians: userIds.length > 0}),
      implCreate("deckGuardian", {deckId: id}),
      implDelete("deckGuardian", {deckId: id}),
    ],
  }),

  "decks.addToSpaceBefore": defineMutation<
    {
      deckIds: DeckId[];
      targetId: DeckId | null;
      targetProjectId: ProjectId;
      targetSpaceId: ProjectSpaceId;
    },
    void
  >({
    type: "void",
    implicit: ({deckIds, targetSpaceId, targetProjectId}) => [
      ...deckIds.map((id) =>
        implUpdate("deck", {
          id,
          projectId: targetProjectId,
          milestoneId: INVALIDATE,
          sortValue: INVALIDATE,
          defaultProjectTagId: INVALIDATE,
          spaceId: targetSpaceId,
        })
      ),
      implUpdate("card", {
        assigneeId: INVALIDATE,
        milestoneId: INVALIDATE,
        sprintId: INVALIDATE,
      }),
      implUpdate("resolvableParticipant", {done: INVALIDATE, lastChangedAt: INVALIDATE}),
      implCreate("activity"),
    ],
  }),

  "decks.addToSpaceAfter": defineMutation<
    {
      deckIds: DeckId[];
      targetId: DeckId | null;
      targetProjectId: ProjectId;
      targetSpaceId: ProjectSpaceId;
    },
    void
  >({
    type: "void",
    implicit: ({deckIds, targetSpaceId, targetProjectId}) => [
      ...deckIds.map((id) =>
        implUpdate("deck", {
          id,
          projectId: targetProjectId,
          milestoneId: INVALIDATE,
          defaultProjectTagId: INVALIDATE,
          sortValue: INVALIDATE,
          spaceId: targetSpaceId,
        })
      ),
      implUpdate("card", {
        assigneeId: INVALIDATE,
        milestoneId: INVALIDATE,
        sprintId: INVALIDATE,
      }),
      implUpdate("resolvableParticipant", {done: INVALIDATE, lastChangedAt: INVALIDATE}),
      implCreate("activity"),
    ],
  }),

  "accounts.update": defineMutation<
    {
      id: AccountId;
      name?: string;
      subdomain?: string;
      billingName?: string | null;
      billingLine1?: string | null;
      billingLine2?: string | null;
      billingZip?: string | null;
      billingCity?: string | null;
      billingCountryCode?: string | null;
      billingEmail?: string | null;
      priorityLabels?: {a: string; b: string; c: string};
      effortScale?: number[];
      statusChangeDurations?: {snooze: number; archive: number | null};
      timeTrackingMode?: "none" | "manual" | "strict";
      maxTimeTrackingSegmentMsDuration?: number;
      timeTrackingSwimLaneInfo?: boolean;
      workdays?: Workdays;
      workflowMode?: "only_parent_cards" | "journeys";
      persona?: "soloGameDev" | "indieGameDev" | "iiiGameDev" | "aaaGameDev" | "other" | null;
      milestonesEnabled?: boolean;
      dueDateEnabled?: boolean;
      sprintsEnabled?: boolean;
      visionBoardEnabled?: boolean;
      maxHandSlotCount?: number;
      allowInheritHeroCover?: boolean;
      attachmentCoverMode?: "none" | "first" | "last";
      timelineScaleType?: "week" | "day";
      startWeekday?: "monday" | "sunday" | "saturday";
      staffQuestMode?: "full" | "observer";
      dependenciesEnabled?: boolean;
      vatId?: string | null;
      staffPermission?: "full" | "limited";
      hideCompletedCardCountForDecks?: boolean;
      fallbackEffort?: number;
      defaultInboxDeckId?: DeckId | null;
    },
    void
  >({
    type: "update",
    model: "account",
    implicit: ({id, defaultInboxDeckId}) => [
      implUpdate("stripeAccountSync", {
        id,
        euVatIdData: INVALIDATE,
        vatTaxPercentage: INVALIDATE,
      }),
      implCreate("stripeAccountSync", {
        id,
        euVatIdData: INVALIDATE,
        vatTaxPercentage: INVALIDATE,
      }),
      ...(defaultInboxDeckId !== undefined
        ? [implUpdate("accountUserSetting", {inboxDeckId: INVALIDATE})]
        : []),
    ],
  }),
  "accounts.updateSettings": defineMutation<
    {inboxDeckId?: DeckId | null; userId?: UserId},
    void,
    {accountId: AccountId}
  >({
    type: "update",
    model: "accountUserSetting",
  }),
  "accounts.disable": defineMutation<
    {password: string; reason?: string | null; text?: string | null; id: AccountId},
    void
  >({
    type: "update",
    model: "account",
    implicit: ({id}) => [
      implUpdate("account", {
        id,
        disabledBy: INVALIDATE,
        isDisabled: INVALIDATE,
        disabledAt: INVALIDATE,
      }),
    ],
  }),

  "projects.create": defineMutation<
    {
      name: string;
      defaultUserAccess: "limited" | "everyone";
      fileId?: FileId | null;
      templateId?: string | null;
      template?: ProjectTemplate | null;
    },
    {id: ProjectId}
  >({
    type: "create",
    model: "project",
    implicit: () => [
      implCreate("projectOrder"),
      implCreate("projectSelection"),
      implUpdate("account", {activeProjectCount: INVALIDATE}),
    ],
  }),
  "projects.createWithDecks": defineMutation<
    {
      name: string;
      templateId?: string | null;
      template?: ProjectTemplate | null;
      discordInfo?: {
        guildId: DiscordGuildId | null;
        allowProjectNotis: boolean;
        notiChannelId: string | null;
        allowCommand: boolean;
        commandText: string | null;
      } | null;
      emails?: string[];
      source?: {type: string; value?: string | null};
    },
    {projectId: ProjectId}
  >({
    type: "create",
    model: "project",
    implicit: () => [
      implCreate("projectOrder"),
      implCreate("projectSelection"),
      implCreate("deck"),
      implCreate("deckSubscription"),
      implCreate("activity"),
      implUpdate("account", {activeProjectCount: INVALIDATE}),
    ],
  }),
  "projects.update": defineMutation<
    {
      id: ProjectId;
      name?: string;
      allowUpvotes?: boolean;
      isPublic?: boolean;
      commentsArePublic?: boolean;
      publicPath?: string | null;
      publicMessage?: string | null;
      coverFileId?: FileId | null;
      publicBannerFileId?: FileId | null;
      publicTileFileId?: FileId | null;
      publicHeading?: string | null;
      publicRegistryAgreement?: boolean | null;
      publicBackgroundImageId?: FileId | null;
      publicLayoutVersion?: number;
      publicBackgroundColor?: string | null;
      publicIsExplicit?: boolean;
      publicVisionBoardId?: VisionBoardId | null;
      defaultUserAccess?: "limited" | "everyone";
      markerColor?: string | null;
      effortIcon?: string | null;
      explicitUserAccess?: Record<UserId, "default" | "producer">;
      spaces?: {
        id: ProjectSpaceId;
        name: string | null;
        icon: string | null;
        defaultDeckType?: "task" | "hero" | "doc" | "mixed";
      }[];
    },
    void
  >({
    type: "update",
    model: "project",
    implicit: ({id, explicitUserAccess, defaultUserAccess}) => [
      ...(explicitUserAccess !== undefined
        ? [
            implCreate("projectUser"),
            implUpdate("projectUser", {projectRole: INVALIDATE, projectId: id}),
            implCreate("userProjectAccess"),
            implUpdate("userProjectAccess", {projectRole: INVALIDATE, projectId: id}),
          ]
        : []),
      ...(defaultUserAccess === "limited"
        ? [
            implUpdate("accountUserSetting", {inboxDeckId: INVALIDATE}),
            implUpdate("account", {defaultInboxDeckId: INVALIDATE}),
          ]
        : []),
    ],
  }),
  "projects.setVisibility": defineMutation<
    {id: ProjectId; visibility: "deleted" | "default" | "archived"},
    void
  >({
    type: "update",
    model: "project",
    implicit: () => [
      implDelete("projectSelection"),
      implUpdate("project", {isPublic: INVALIDATE}),
      implUpdate("account", {activeProjectCount: INVALIDATE}),
      implUpdate("queueEntry", {sortIndex: INVALIDATE}),
      implDelete("queueEntry"),
    ],
  }),
  "projects.updateCover": defineMutation<{id: ProjectId; coverFileData: CoverFileData}, void>({
    type: "update",
    model: "project",
    implicit: ({id}) => [implUpdate("project", {coverFileId: INVALIDATE, id})],
  }),
  "projects.addTag": defineMutation<
    {projectId: ProjectId; tag: string; color?: string | null; emoji?: string | null},
    {id: ProjectTagId}
  >({
    type: "create",
    model: "projectTag",
    implicit: () => [implUpdate("card", {masterTags: INVALIDATE}), implDelete("userTag")],
  }),
  "projects.deleteTags": defineMutation<{ids: ProjectTagId[]}, void>({
    type: "delete",
    model: "projectTag",
  }),
  "projects.updateTag": defineMutation<
    {
      id: ProjectTagId;
      tag?: string;
      color?: string | null;
      emoji?: string | null;
      description?: string | null;
    },
    void
  >({
    type: "update",
    model: "projectTag",
    implicit: () => [
      implUpdate("card", {masterTags: INVALIDATE, content: INVALIDATE}),
      implDelete("userTag"),
    ],
  }),
  "projects.copyTags": defineMutation<{ids: ProjectTagId[]; targetProjectId: ProjectId}, void>({
    type: "create",
    model: "projectTag",
    implicit: () => [implUpdate("card", {masterTags: INVALIDATE}), implDelete("userTag")],
  }),
  "projects.updateOrder": defineMutation<
    {accountId: AccountId; userId: UserId; ids: ProjectId[]},
    void
  >({
    type: "create",
    model: "projectOrder",
    implicit: () => [implUpdate("projectOrder", {sortIndex: INVALIDATE})],
  }),

  "cardPresets.create": defineMutation<
    {
      accountId: AccountId;
      name: string;
      data: {
        content?: string;
        priority?: "a" | "b" | "c" | null;
        effort?: number | null;
        masterTags?: string[];
      };
    },
    {id: CardPresetId}
  >({
    type: "create",
    model: "cardPreset",
  }),
  "cardPresets.delete": defineMutation<{id: CardPresetId}, void>({
    type: "delete",
    model: "cardPreset",
  }),

  "workflows.apply": defineMutation<{cardId: CardId; itemIds?: WorkflowItemId[]}, {ids: CardId[]}>({
    type: "create",
    model: "card",
    implicit: ({cardId}) => [
      implUpdate("card", {
        id: cardId,
        childCards: INVALIDATE,
        status: INVALIDATE,
        visibility: INVALIDATE,
        derivedStatus: INVALIDATE,
      }),
    ],
  }),

  "workflows.createItem": defineMutation<
    {
      deckId: DeckId;
      content: string;
      priority?: "a" | "b" | "c" | null;
      effort?: number | null;
      assigneeId?: UserId | null;
      targetDeckId?: DeckId | null;
      masterTags?: string[];
      label?: string | null;
    },
    {id: WorkflowItemId}
  >({
    type: "create",
    model: "workflowItem",
  }),
  "workflows.updateItem": defineMutation<
    {
      itemId: WorkflowItemId;
      content?: string;
      priority?: "a" | "b" | "c" | null;
      effort?: number | null;
      assigneeId?: UserId | null;
      targetDeckId?: DeckId | null;
      masterTags?: string[];
      visibility?: "default" | "archived" | "deleted";
      inDeps?: WorkflowItemId[];
      outDeps?: WorkflowItemId[];
    },
    void
  >({
    type: "update",
    model: "workflowItem",
    implicit: ({itemId, inDeps, outDeps, visibility}) => [
      implCreate("workflowItemHistory", {itemId}),
      ...(inDeps || outDeps || visibility
        ? [implUpdate("workflowItem", {inDeps: INVALIDATE, outDeps: INVALIDATE})]
        : []),
    ],
  }),
  "workflows.bulkUpdateItems": defineMutation<
    {
      ids: WorkflowItemId[];
      content?: string;
      priority?: "a" | "b" | "c" | null;
      effort?: number | null;
      assigneeId?: UserId | null;
      targetDeckId?: DeckId | null;
      addMasterTag?: string;
      removeMasterTag?: string;
      visibility?: "default" | "archived" | "deleted";
      deckId?: DeckId;
      label?: string;
    },
    void
  >({
    type: "update",
    model: "workflowItem",
    implicit: ({ids, addMasterTag, removeMasterTag, deckId}) => [
      ...ids.map((itemId) => implCreate("workflowItemHistory", {itemId})),
      // moving steps to another deck drops their dependencies (see `updateWorkflowItem`), which
      // also removes them from the dep lists of steps we don't know here. So invalidate every
      // cached step – but never write empty lists, as those get read back by the dependency
      // editor's read-modify-write and would then be persisted.
      ...(deckId ? [implUpdate("workflowItem", {inDeps: INVALIDATE, outDeps: INVALIDATE})] : []),
      ...(addMasterTag || removeMasterTag
        ? ids.map((itemId) => implUpdate("workflowItem", {itemId, masterTags: INVALIDATE}))
        : []),
    ],
  }),
  "workflows.renameItemZone": defineMutation<
    {deckId: DeckId} & ZoneRename,
    void,
    ZoneRenameSupport<WorkflowItemId>
  >({
    type: "void",
    implicit: (data) => [
      implUpdate("deck", {id: data.deckId, workflowItemOrderLabels: renamedZoneLabels(data)}),
      ...(data.contentIds
        ? data.contentIds.map((itemId) =>
            implUpdate("workflowItem", {itemId, label: data.newLabel})
          )
        : [implUpdate("workflowItem", {label: INVALIDATE})]),
    ],
  }),
  "workflows.copyWorkflowItems": defineMutation<{ids: WorkflowItemId[]; deckId: DeckId}, void>({
    type: "create",
    model: "workflowItem",
    // one call makes many rows, and the default step would show a single row with only `deckId`
    optimistic: () => {},
    implicit: ({deckId}) => [implUpdate("deck", {id: deckId, workflowItemOrderLabels: INVALIDATE})],
  }),
  "workflows.createDependencyChain": defineMutation<{ids: WorkflowItemId[]}, void>({
    type: "void",
    implicit: ({ids}) => [
      implUpdate("workflowItem", {ids, inDeps: INVALIDATE, outDeps: INVALIDATE}),
    ],
  }),

  "workflows.addItemsAfter": defineMutation<
    {
      deckId: DeckId;
      targetId: WorkflowItemId | null;
      itemIds: WorkflowItemId[];
      label: string | null;
    },
    void
  >({
    type: "void",
    implicit: ({itemIds, label}) => [
      ...itemIds.map((itemId) =>
        implUpdate("workflowItem", {itemId, label, sortValue: INVALIDATE})
      ),
    ],
  }),
  "workflows.addItemsBefore": defineMutation<
    {
      deckId: DeckId;
      targetId: WorkflowItemId | null;
      itemIds: WorkflowItemId[];
      label: string | null;
    },
    void
  >({
    type: "void",
    implicit: ({itemIds, label}) => [
      ...itemIds.map((itemId) =>
        implUpdate("workflowItem", {itemId, label, sortValue: INVALIDATE})
      ),
    ],
  }),

  "workflowTemplates.create": defineMutation<
    {
      sourceDeckId: DeckId;
      name: string;
      description: string;
      authorName?: string | null;
      tags: string[];
      deckLabelMap: Record<string, string>;
    },
    {id: WorkflowTemplateId}
  >({
    type: "create",
    model: "workflowTemplate",
  }),
  "workflowTemplates.update": defineMutation<
    {
      id: WorkflowTemplateId;
      patch: {
        name?: string;
        description?: string;
        authorName?: string | null;
        tags?: string[];
        itemOrderLabels?: string[];
        deckLabelMap?: Record<string, string>;
      };
      itemDiff: {
        create: {
          id: WorkflowTemplateItemId;
          sortValue: string;
          orderLabel: string;
          content: string;
          title: string;
          priority?: "a" | "b" | "c" | null;
          effort?: number | null;
          sourceTargetDeckId: DeckId | null;
          sourceWorkflowItemId: WorkflowItemId;
          tags: string[];
          inDeps: WorkflowTemplateItemId[];
          outDeps: WorkflowTemplateItemId[];
        }[];
        update: {
          id: WorkflowTemplateItemId;
          patch: {
            sortValue?: string;
            orderLabel?: string;
            content?: string;
            title?: string;
            priority?: "a" | "b" | "c" | null;
            effort?: number | null;
            sourceTargetDeckId?: DeckId | null;
            tags?: string[];
            inDeps?: WorkflowTemplateItemId[];
            outDeps?: WorkflowTemplateItemId[];
          };
        }[];
        delete: WorkflowTemplateItemId[];
      };
    },
    void
  >({
    type: "update",
    model: "workflowTemplate",
    implicit: ({id, itemDiff}) => [
      implUpdate("workflowTemplate", {
        id,
        status: INVALIDATE,
        statusMeta: INVALIDATE,
        lastUpdatedAt: INVALIDATE,
        itemOrderLabels: INVALIDATE,
        // do NOT pass `items: INVALIDATE`: the delete entries below filter the
        // parent's `items` array via cache.ts:637, and a queued INVALIDATE
        // would crash that filter. Instead, each implDelete/implCreate triggers
        // `findAffectedRelations` which sets `status.items = "invalid"` on the
        // parent template (cache.ts:753-756) — that gives us the refetch.
      }),
      ...itemDiff.delete.map((itemId) => implDelete("workflowTemplateItem", {id: itemId})),
      // pass templateId so findAffectedRelations resolves the parent template
      // instead of falling through to "invalidate every workflowTemplate".
      ...itemDiff.create.map(() => implCreate("workflowTemplateItem", {templateId: id})),
      ...itemDiff.update.map(({id: itemId, patch}) =>
        implUpdate("workflowTemplateItem", {
          id: itemId,
          title: INVALIDATE,
          content: INVALIDATE,
          priority: INVALIDATE,
          effort: INVALIDATE,
          orderLabel: INVALIDATE,
          sortValue: INVALIDATE,
          tags: INVALIDATE,
          sourceTargetDeckId: INVALIDATE,
          lastUpdatedAt: INVALIDATE,
          // hasMany relations must hold real arrays so a sibling's delete entry
          // can `.filter` them (cache.ts:637).
          ...(patch.inDeps !== undefined && {inDeps: patch.inDeps}),
          ...(patch.outDeps !== undefined && {outDeps: patch.outDeps}),
        })
      ),
    ],
  }),
  "workflowTemplates.delete": defineMutation<{id: WorkflowTemplateId}, void>({
    type: "delete",
    model: "workflowTemplate",
  }),
  "workflowTemplates.clone": defineMutation<
    {
      templateId: WorkflowTemplateId;
      targetDeckId: DeckId;
      mode: "replace" | "append";
      targetDeckMap: Record<string, DeckId | null>;
    },
    {ids: WorkflowItemId[]; newDeckIds: DeckId[]}
  >({
    type: "create",
    model: "workflowItem",
    implicit: ({templateId, targetDeckId, mode}, opts) => [
      implUpdate("deck", {id: targetDeckId, workflowItemOrderLabels: INVALIDATE}),
      implUpdate("workflowTemplate", {id: templateId, cloneCount: INVALIDATE}),
      ...(!opts._isOptimistic && opts.newDeckIds.length ? [implCreate("deck")] : []),
      ...(mode === "replace"
        ? [
            implUpdate("workflowItem", {
              visibility: INVALIDATE,
              inDeps: INVALIDATE,
              outDeps: INVALIDATE,
            }),
          ]
        : []),
    ],
  }),

  "attachments.deleteFile": defineMutation<{id: AttachmentId}, void, {fileId: FileId}>({
    type: "update",
    model: "attachment",
    implicit: ({fileId}) => [
      implUpdate("file", {
        id: fileId,
        isDeleted: true,
        deletedBy: INVALIDATE,
        deletedAt: INVALIDATE,
      }),
      implUpdate("account", {totalMediaByteUsage: INVALIDATE}),
    ],
  }),
  "attachments.batchDeleteFile": defineMutation<{ids: AttachmentId[]; fileIds: FileId[]}, void>({
    type: "update",
    model: "attachment",
    implicit: ({fileIds}) => [
      ...fileIds.map((fileId) =>
        implUpdate("file", {
          id: fileId,
          isDeleted: true,
          deletedBy: INVALIDATE,
          deletedAt: INVALIDATE,
        })
      ),
      implUpdate("account", {totalMediaByteUsage: INVALIDATE}),
    ],
  }),
  "attachments.batchDeleteByFileId": defineMutation<{ids: FileId[]}, void>({
    type: "void",
    implicit: ({ids}) => [
      ...ids.map((id) =>
        implUpdate("file", {
          id,
          isDeleted: true,
          deletedBy: INVALIDATE,
          deletedAt: INVALIDATE,
        })
      ),
      implUpdate("account", {totalMediaByteUsage: INVALIDATE}),
    ],
  }),
  "attachments.update": defineMutation<{id: AttachmentId; content: string}, void>({
    type: "update",
    model: "attachment",
  }),
  "attachments.delete": defineMutation<{id: AttachmentId; cardId: CardId}, void>({
    type: "delete",
    model: "attachment",
    implicit: ({cardId}) => [
      implUpdate("card", {attachments: INVALIDATE, coverFileId: INVALIDATE, cardId}),
      implCreate("cardHistory", {cardId}),
      implCreate("activity", {cardId}),
    ],
  }),

  "resolvables.create": defineMutation<
    {
      cardId: CardId;
      context: "block" | "review" | "comment";
      content: string;
    },
    {id: ResolvableId}
  >({
    type: "create",
    model: "resolvable",
    implicit: ({cardId, context}) => [
      implUpdate("card", {
        status: INVALIDATE,
        derivedStatus:
          context === "block" ? "blocked" : context === "review" ? "review" : INVALIDATE,
        cardId,
      }),
      implCreate("resolvableEntry", {cardId}),
      implCreate("activity", {cardId}),
      implUpdate("timeTrackingSegment", {finishedAt: INVALIDATE}),
      implDelete("timeTrackingSegment"),
      implUpdate("timeTrackingSum", {
        sumMs: INVALIDATE,
        runningModifyDurationMsBy: INVALIDATE,
        runningStartedAt: INVALIDATE,
      }),
      implDelete("timeTrackingSum"),
      implDelete("activeTimeTracker", {cardId}),
      implCreate("resolvableParticipant"),
      ...handUpdates,
    ],
  }),
  "resolvables.close": defineMutation<
    {id: ResolvableId; markCardDone?: boolean},
    {cardId: CardId},
    {closedBy: UserId}
  >({
    type: "update",
    model: "resolvable",
    implicit: ({id, markCardDone}, opts) => {
      const getDoneCardId = (): CardId | null => {
        if (!markCardDone) return null;
        if (!opts._isOptimistic) return opts.cardId;
        // stored under whichever name the query asked for
        const resolvable = opts._getModelFromCache("resolvable", id);
        return ((resolvable?.card ?? resolvable?.cardId) as CardId | undefined) ?? null;
      };
      const doneCardId = getDoneCardId();
      return [
        implUpdate("resolvable", {
          id,
          closedAt: INVALIDATE,
          isClosed: true,
          reopenedBy: null,
          reopenedAt: null,
        }),
        implUpdate("timeTrackingSegment", {finishedAt: INVALIDATE}),
        implDelete("timeTrackingSegment"),
        implUpdate("timeTrackingSum", {
          sumMs: INVALIDATE,
          runningModifyDurationMsBy: INVALIDATE,
          runningStartedAt: INVALIDATE,
        }),
        implDelete("timeTrackingSum"),
        implUpdate("card", {derivedStatus: INVALIDATE}),
        ...(doneCardId
          ? [
              implUpdate("card", {cardId: doneCardId, status: "done"}),
              ...singleCardUpdate({id: doneCardId, status: "done"}, opts),
            ]
          : []),
      ];
    },
  }),
  "resolvables.reopen": defineMutation<{id: ResolvableId}, void>({
    type: "update",
    model: "resolvable",
    implicit: ({id}) => [
      implUpdate("timeTrackingSegment", {finishedAt: INVALIDATE}),
      implDelete("timeTrackingSegment"),
      implUpdate("card", {derivedStatus: INVALIDATE}),
      implUpdate("resolvable", {
        id,
        closedAt: INVALIDATE,
        isClosed: false,
        reopenedBy: INVALIDATE,
        reopenedAt: INVALIDATE,
      }),
      implUpdate("timeTrackingSum", {
        sumMs: INVALIDATE,
        runningModifyDurationMsBy: INVALIDATE,
        runningStartedAt: INVALIDATE,
      }),
      implDelete("timeTrackingSum"),
      implDelete("activeTimeTracker"),
      implUpdate("card", {status: INVALIDATE}),
    ],
  }),
  "resolvables.comment": defineMutation<
    {resolvableId: ResolvableId; content: string},
    {id: ResolvableEntryId},
    {authorId: UserId}
  >({
    type: "create",
    model: "resolvableEntry",
    // the data carries no timestamps, and a missing one reads as the time the page loaded
    optimistic: (tx, {resolvableId, authorId, content}) => {
      const now = new Date();
      tx.create("resolvableEntry", {
        resolvableId,
        authorId,
        content,
        createdAt: now,
        lastChangedAt: now,
      });
    },
    implicit: ({resolvableId}) => [
      implCreate("resolvableParticipant", {resolvableId}),
      implCreate("activity"),
      implUpdate("resolvableParticipant", {
        done: INVALIDATE,
        lastChangedAt: INVALIDATE,
        resolvableId,
      }),
      implCreate("resolvableParticipantHistory", {resolvableId}),
    ],
  }),
  "resolvables.updateComment": defineMutation<
    {entryId: ResolvableEntryId; content: string},
    void,
    {resolvableId: ResolvableId}
  >({
    type: "update",
    model: "resolvableEntry",
    implicit: ({entryId, resolvableId}) => [
      implCreate("resolvableParticipant", {resolvableId}),
      implCreate("activity"),
      implUpdate("resolvableParticipant", {
        done: INVALIDATE,
        lastChangedAt: INVALIDATE,
        resolvableId,
      }),
      implCreate("resolvableParticipantHistory", {resolvableId}),
      implCreate("resolvableEntryHistory", {entryId}),
    ],
  }),
  "resolvables.updateParticipantDone": defineMutation<
    {
      resolvableId: ResolvableId;
      userId: UserId;
      done: boolean;
      status?: "active" | "opt_out" | "approve";
    },
    void
  >({
    type: "update",
    model: "resolvableParticipant",
    implicit: ({resolvableId}) => [
      implUpdate("resolvableParticipant", {lastChangedAt: INVALIDATE}),
      implCreate("resolvableParticipantHistory", {resolvableId}),
    ],
  }),
  "resolvables.addReaction": defineMutation<
    {entryId: ResolvableEntryId; value: {type: "emoji"; value: string}},
    {id: ResolvableEntryReactionId}
  >({
    type: "create",
    model: "resolvableEntryReaction",
  }),
  "resolvables.removeReaction": defineMutation<{reactionId: ResolvableEntryReactionId}, void>({
    type: "delete",
    model: "resolvableEntryReaction",
  }),

  "users.update": defineMutation<
    {
      id: UserId;
      name?: string;
      fullName?: string | null;
      showCardIdInTimer?: boolean;
      disableMovingImages?: boolean;
      autoMeFilterCardLimit?: number | null;
      lastSeenReleaseId?: ReleaseId;
      timezone?: string;
      statusColorPalette?: "default" | "protanopia" | "protanomaly" | "deuteranopia";
      wantsNewsletter?: boolean;
      wantsDailyDigestMail?: boolean;
      wantsConvoDigestMail?: boolean;
      wantsWeeklyDigestMail?: boolean;
      disableAnimations?: boolean;
      enableClickToEditCards?: boolean;
      profileImageData?: CoverFileData | null;
      timelineScaleTypeOverwrite?: "day" | "week" | null;
      startWeekdayOverwrite?: "monday" | "sunday" | "saturday" | null;
      timeFormat?: "12h" | "24h" | null;
    },
    void,
    {accountId?: AccountId}
  >({
    type: "update",
    model: "user",
    implicit: ({
      id,
      accountId,
      profileImageData,
      wantsWeeklyDigestMail,
      timelineScaleTypeOverwrite,
      startWeekdayOverwrite,
    }) => [
      ...(wantsWeeklyDigestMail !== undefined ||
      timelineScaleTypeOverwrite !== undefined ||
      startWeekdayOverwrite !== undefined
        ? [
            implUpdate("accountUserSetting", {
              userId: id,
              accountId,
              wantsWeeklyDigestMail,
              timelineScaleTypeOverwrite,
              startWeekdayOverwrite,
            }),
          ]
        : []),
      ...(profileImageData ? [implUpdate("user", {profileImageId: INVALIDATE, id})] : []),
    ],
  }),
  "users.changePassword": defineMutation<
    {oldPassword: string; password: string},
    void,
    {userId: UserId}
  >({
    type: "update",
    model: "user",
    implicit: ({userId}) => [implUpdate("user", {id: userId, hasPassword: true})],
  }),
  "users.login": defineMutation<
    {email: string; password: string},
    {secondFactorRequired: false} | SecondFactorRequired
  >({
    type: "create",
    model: "user",
  }),
  "users.logout": defineMutation<Record<string, never>, void>({
    type: "delete",
    model: "user",
  }),

  "users.sendInvitation": defineMutation<
    {
      emails: string[];
      role: "observer" | "admin" | "guest";
      accessibleProjectIds: ProjectId[];
    },
    {user: number; invite: number},
    {accountId: AccountId}
  >({
    type: "create",
    model: "userInvitation",
    implicit: ({accountId}, opts) =>
      !opts._isOptimistic && opts.user > 0
        ? [
            implCreate("accountRole", {accountId}),
            implUpdate("account", {id: accountId, activeUsers: INVALIDATE}),
          ]
        : [],
  }),
  "users.revokeInvitation": defineMutation<{id: UserInvitationId}, void>({
    type: "delete",
    model: "userInvitation",
  }),
  "users.resendInvitation": defineMutation<{id: UserInvitationId}, void>({
    type: "update",
    model: "userInvitation",
    implicit: ({id}) => [implUpdate("userInvitation", {id, createdAt: INVALIDATE})],
  }),
  "users.joinAccount": defineMutation<
    {token: string; name: string; password: string} & {accountId: AccountId},
    {id: UserId}
  >({
    type: "create",
    model: "user",
    implicit: ({accountId}) => [
      implCreate("accountRole", {accountId}),
      implUpdate("account", {id: accountId, activeUsers: INVALIDATE}),
      implDelete("userInvitation", {accountId}),
    ],
  }),
  "users.requestPasswordReset": defineMutation<{email: string}, void>({
    type: "update",
    model: "user",
  }),
  "users.resetPassword": defineMutation<{token: string; password: string}, void>({
    type: "update",
    model: "user",
  }),
  "users.beginResetLogin": defineMutation<{token: string}, SecondFactorRequired>({type: "void"}),

  "secondFactor.beginPasskey": defineMutation<
    Record<string, never>,
    PublicKeyCredentialRequestOptionsJSON
  >({type: "void"}),
  "secondFactor.verifyPasskey": defineMutation<{credential: unknown}, {ok: true}>({type: "void"}),
  "secondFactor.verifyRecoveryCode": defineMutation<{code: string}, {ok: true}>({
    type: "void",
    implicit: () => [implUpdate("user", {unusedRecoveryCodeCount: INVALIDATE})],
  }),
  "secondFactor.verifyTotp": defineMutation<{code: string}, {ok: true}>({
    type: "void",
    implicit: () => [implUpdate("user", SELF_SECURITY_FIELDS)],
  }),
  "secondFactor.cancel": defineMutation<Record<string, never>, {ok: true}>({type: "void"}),

  "users.reverify": defineMutation<{password: string}, void>({type: "void"}),
  "users.reverifyTotp": defineMutation<{code: string}, void>({
    type: "void",
    implicit: () => [implUpdate("user", SELF_SECURITY_FIELDS)],
  }),
  "users.beginReverifyPasskey": defineMutation<
    Record<string, never>,
    PublicKeyCredentialRequestOptionsJSON
  >({type: "void"}),
  "users.finishReverifyPasskey": defineMutation<{credential: unknown}, void>({type: "void"}),

  "passkeys.beginRegistration": defineMutation<
    Record<string, never>,
    PublicKeyCredentialCreationOptionsJSON
  >({type: "void"}),
  // `recoveryCodes` only for the first key
  "passkeys.finishRegistration": defineMutation<
    {credential: unknown; name: string},
    {passkeyId: PasskeyId; recoveryCodes?: string[]}
  >({
    type: "create",
    model: "passkey",
    implicit: () => [
      implUpdate("user", {hasSecondFactor: INVALIDATE, unusedRecoveryCodeCount: INVALIDATE}),
    ],
  }),
  "passkeys.rename": defineMutation<{id: PasskeyId; name: string}, void>({
    type: "update",
    model: "passkey",
  }),
  // the last key takes the recovery codes with it
  "passkeys.delete": defineMutation<{id: PasskeyId}, void>({
    type: "delete",
    model: "passkey",
    implicit: () => [
      implUpdate("user", {hasSecondFactor: INVALIDATE, unusedRecoveryCodeCount: INVALIDATE}),
    ],
  }),
  "totp.beginSetup": defineMutation<Record<string, never>, {otpauthUri: string; secret: string}>({
    type: "void",
  }),
  // `recoveryCodes` only for the first factor
  "totp.confirmSetup": defineMutation<{code: string}, {recoveryCodes?: string[]}>({
    type: "void",
    implicit: () => [implUpdate("user", SELF_SECURITY_FIELDS)],
  }),
  // the last factor takes the recovery codes with it
  "totp.delete": defineMutation<Record<string, never>, void>({
    type: "void",
    implicit: () => [implUpdate("user", SELF_SECURITY_FIELDS)],
  }),
  "recoveryCodes.regenerate": defineMutation<Record<string, never>, {recoveryCodes: string[]}>({
    type: "void",
    implicit: () => [implUpdate("user", {unusedRecoveryCodeCount: INVALIDATE})],
  }),

  "roles.update": defineMutation<
    {userId: UserId; role: "admin" | "guest" | "observer"},
    void,
    {accountId: AccountId}
  >({
    type: "update",
    model: "accountRole",
    implicit: ({userId, accountId}) => [
      implDelete("userProjectAccess", {userId}),
      implDelete("projectUser", {userId}),
      implUpdate("account", {activeUsers: INVALIDATE}),
      implUpdate("accountRole", {accountId, role: INVALIDATE}),
    ],
  }),
  // handing the org over is its own action: it also demotes the caller, and it is browser-only
  "roles.transferOwnership": defineMutation<{userId: UserId}, void, {accountId: AccountId}>({
    type: "update",
    model: "accountRole",
    implicit: ({userId, accountId}) => [
      implDelete("userProjectAccess", {userId}),
      implDelete("projectUser", {userId}),
      implUpdate("account", {activeUsers: INVALIDATE}),
      implUpdate("accountRole", {accountId, role: INVALIDATE}), // setting an owner de-owns myself
    ],
  }),
  "roles.disable": defineMutation<{userId: UserId}, void, {accountId: AccountId}>({
    type: "update",
    model: "accountRole",
    implicit: ({userId, accountId}) => [
      implDelete("userProjectAccess", {userId}),
      implDelete("projectUser", {userId}),
      implUpdate("account", {activeUsers: INVALIDATE}),
      implUpdate("accountRole", {userId, accountId, role: INVALIDATE}),
    ],
  }),
  "roles.leave": defineMutation<{}, void, {userId: UserId; accountId: AccountId}>({
    type: "update",
    model: "accountRole",
    implicit: ({userId, accountId}) => [
      implDelete("userProjectAccess", {userId}),
      implDelete("projectUser", {userId}),
      implUpdate("account", {activeUsers: INVALIDATE}),
      implUpdate("accountRole", {userId, accountId, role: INVALIDATE}),
    ],
  }),
  "roles.reEnable": defineMutation<{userId: UserId}, void, {accountId: AccountId}>({
    type: "update",
    model: "accountRole",
    implicit: ({userId, accountId}) => [
      implDelete("userProjectAccess", {userId}),
      implDelete("projectUser", {userId}),
      implUpdate("account", {activeUsers: INVALIDATE}),
      implUpdate("accountRole", {userId, accountId, role: INVALIDATE}),
    ],
  }),
  "roles.delete": defineMutation<{userId: UserId}, void, {accountId: AccountId}>({
    type: "delete",
    model: "accountRole",
    implicit: ({userId, accountId}) => [
      implDelete("userProjectAccess", {userId}),
      implDelete("projectUser", {userId}),
      implUpdate("account", {activeUsers: INVALIDATE}),
      implUpdate("accountRole", {userId, accountId, role: INVALIDATE}),
    ],
  }),
  // no via, since to the receiving user it's like actually adding/removing a project
  "roles.addProjectAccess": defineMutation<{userId: UserId; projectId: ProjectId}, void>({
    type: "create",
    model: "projectUser",
    implicit: ({userId, projectId}) => [implCreate("userProjectAccess", {userId, projectId})],
  }),
  "roles.revokeProjectAccess": defineMutation<{userId: UserId; projectId: ProjectId}, void>({
    type: "delete",
    model: "projectUser",
    implicit: ({userId, projectId}) => [implDelete("userProjectAccess", {userId, projectId})],
  }),
  "roles.setAddonRoles": defineMutation<
    {userId: UserId; addRoles: MemberAddonRole[]},
    void,
    {accountId: AccountId}
  >({
    type: "update",
    model: "accountRole",
  }),

  "userEmails.add": defineMutation<
    {password: string; email: string},
    {id: UserEmailId},
    {userId: UserId}
  >({
    type: "create",
    model: "userEmail",
  }),
  "userEmails.delete": defineMutation<{id: UserEmailId}, void>({
    type: "delete",
    model: "userEmail",
  }),
  "userEmails.setPrimary": defineMutation<{id: UserEmailId}, void>({
    type: "update",
    model: "userEmail",
    implicit: () => [implUpdate("userEmail", {isPrimary: INVALIDATE})],
  }),
  "userEmails.resendVerification": defineMutation<{id: UserEmailId}, void>({
    type: "update",
    model: "userEmail",
  }),
  "userEmails.verify": defineMutation<{token: string}, {id: UserEmailId; justLoggedIn: boolean}>({
    type: "update",
    model: "userEmail",
    implicit: (_, opts) => [
      implUpdate(
        "userEmail",
        opts._isOptimistic ? {isVerified: INVALIDATE} : {isVerified: INVALIDATE, id: opts.id}
      ),
      implCreate("accountRole"),
      implDelete("userInvitation"),
    ],
  }),

  "inviteCodes.create": defineMutation<Record<string, never>, void>({
    type: "create",
    model: "userInviteCode",
  }),
  "inviteCodes.disable": defineMutation<{id: UserInviteCodeId}, void>({
    type: "void",
    implicit: ({id}) => [implUpdate("userInviteCode", {id, isActive: false})],
  }),

  "inviteCodes.apply": defineMutation<
    {token: string; email: string; name: string; password: string},
    void
  >({
    type: "void",
    implicit: () => [
      implUpdate("userInviteCode", {useCount: INVALIDATE}),
      implCreate("accountRole"),
      implUpdate("account", {activeUsers: INVALIDATE}),
    ],
  }),
  "inviteCodes.applyExisting": defineMutation<{token: string}, void>({
    type: "void",
    implicit: () => [
      implUpdate("userInviteCode", {useCount: INVALIDATE}),
      implCreate("accountRole"),
      implUpdate("account", {activeUsers: INVALIDATE}),
    ],
  }),

  "userTags.add": defineMutation<
    {accountId: AccountId; tag: string},
    {id: UserTagId},
    {userId: UserId}
  >({
    type: "create",
    model: "userTag",
  }),
  "userTags.delete": defineMutation<{id: UserTagId}, void>({
    type: "delete",
    model: "userTag",
  }),
  "userTags.update": defineMutation<{id: UserTagId; tag: string}, void>({
    type: "update",
    model: "userTag",
  }),

  "bookmarks.addCards": defineMutation<{ids: CardId[]; userId: UserId}, void>({
    type: "create",
    model: "handCard",
  }),
  "bookmarks.removeCards": defineMutation<
    {ids: CardId[]; userId: UserId},
    void,
    {accountId: AccountId}
  >({
    type: "void",
    // `ids` are card ids, not handCard ids
    implicit: ({ids, userId, accountId}) =>
      ids.map((cardId) => implDelete("handCard", {cardId, userId, accountId})),
  }),
  "bookmarks.setOrders": defineMutation<{cardIds: CardId[]; userId: UserId}, void>({
    type: "void",
    implicit: () => [implCreate("handCard"), implUpdate("handCard", {sortIndex: INVALIDATE})],
  }),
  "handQueue.addCardsToHand": defineMutation<
    {cardIds: CardId[]; userId: UserId},
    {queueEntries: {id: QueueEntryId; cardId: CardId}[]}
  >({
    type: "create",
    model: "queueEntry",
    implicit: ({cardIds, userId}, {_isOptimistic}) => [
      ...handUpdates,
      implUpdate("card", {ids: cardIds, assigneeId: INVALIDATE, derivedStatus: INVALIDATE}),
      // without it the card only shows up in the hand once the save and the reload are back
      ...(_isOptimistic
        ? [placeInHandOptimistically({userId, placement: {type: "append", cardIds}})]
        : []),
    ],
  }),
  "handQueue.removeCards": defineMutation<{cardIds: CardId[]; userId?: UserId}, void>({
    type: "delete",
    model: "queueEntry",
    implicit: ({cardIds, userId}, {_isOptimistic}) => [
      implUpdate("queueEntry", {sortIndex: INVALIDATE}),
      // the call names cards, not entry ids, so the plain delete can't drop anything up front
      ...(_isOptimistic ? [removeFromHandsOptimistically({cardIds, userId})] : []),
    ],
  }),
  "handQueue.setCardOrders": defineMutation<
    {cardIds: CardId[]; draggedCardIds: CardId[]; userId: UserId},
    {queueEntries: {id: QueueEntryId; cardId: CardId}[]}
  >({
    type: "void",
    implicit: ({cardIds, draggedCardIds, userId}, {_isOptimistic}) => [
      ...handUpdates,
      implUpdate("card", {ids: cardIds, assigneeId: INVALIDATE, derivedStatus: INVALIDATE}),
      ...(_isOptimistic
        ? [placeInHandOptimistically({userId, placement: {type: "order", cardIds, draggedCardIds}})]
        : []),
    ],
  }),
  "handQueue.setQueueSelection": defineMutation<{queueUserIds: UserId[]; userId: UserId}, void>({
    type: "void",
    implicit: () => [
      implUpdate("queueSelection", {sortIndex: INVALIDATE}),
      implCreate("queueSelection"),
    ],
  }),

  "notifications.dismissCardDiffs": defineMutation<{cardIds: CardId[]}, void, {userId: UserId}>({
    type: "void",
    implicit: ({userId, cardIds}) =>
      cardIds.map((cardId) => implDelete("cardDiffNotification", {cardId, userId})),
  }),
  "notifications.dismissAutoFinishedTimeTrackingSegment": defineMutation<
    {},
    void,
    {userId: UserId; accountId: AccountId}
  >({
    type: "delete",
    model: "autoFinishedTimeTrackingSegment",
  }),
  "notifications.dismissResolvables": defineMutation<
    {resolvableIds: ResolvableId[]},
    void,
    {userId: UserId}
  >({
    type: "void",
    // a snoozing notification is snoozed again for three days, the others go away
    optimistic: (tx, {userId, resolvableIds}) => {
      for (const resolvableId of resolvableIds) {
        const id = getIdAsKey([userId, resolvableId]);
        const isSnoozing = tx.get<boolean | null>("resolvableNotification", id, "isSnoozing", null);
        if (isSnoozing === null) continue;
        if (isSnoozing) {
          tx.update("resolvableNotification", id, {
            snoozeUntil: new Date(Date.now() + toMs.days(3)),
          });
        } else {
          tx.delete("resolvableNotification", id);
        }
      }
    },
    // Only on resolve: an optimistic implDelete would also delete the snoozing rows. Invalidating a
    // row that still exists just reloads it along with its lists.
    implicit: ({userId, resolvableIds}, {_isOptimistic}) =>
      _isOptimistic
        ? []
        : resolvableIds.map((resolvableId) =>
            implDelete("resolvableNotification", {userId, resolvableId})
          ),
  }),
  "notifications.seenResolvableEntries": defineMutation<
    {resolvableId: ResolvableId; latestSeenEntryId: ResolvableEntryId},
    void,
    {userId: UserId}
  >({
    type: "update",
    model: "resolvableNotification",
  }),
  "notifications.setRemindMeOn": defineMutation<
    {resolvableIds: ResolvableId[]; isMuted: boolean; remindMeOn: Date | null},
    void,
    {userId: UserId}
  >({
    type: "void",
    implicit: ({resolvableIds, userId, isMuted, remindMeOn}) =>
      resolvableIds.map((id) =>
        implUpdate("resolvableNotification", {
          userId,
          resolvableId: id,
          isMuted,
          remindMeOn,
        })
      ),
  }),
  "notifications.addBrowserSubscription": defineMutation<{subscription: PushSubscription}, void>({
    type: "void",
  }),
  "notifications.removeBrowserSubscription": defineMutation<{subscription: PushSubscription}, void>(
    {
      type: "void",
    }
  ),
  "notifications.setChannels": defineMutation<
    {settings: {channel: "browser"; enableConversations: boolean}[]},
    void
  >({
    type: "create",
    model: "userNotificationChannel",
    implicit: (data) => [
      implDelete(
        "userNotificationChannel",
        data as unknown as ImplicitData<"userNotificationChannel">
      ),
    ],
  }),

  "watchings.addDeck": defineMutation<{deckId: DeckId; userId: UserId}, {id: DeckSubscriptionId}>({
    type: "create",
    model: "deckSubscription",
    // the data has no account, which the button's list `account.deckSubscriptions` is keyed by
    optimistic: (tx, {deckId, userId}) => {
      const accountId = tx.get("_root", "_root", "account");
      tx.create("deckSubscription", {deck: deckId, user: userId, account: accountId});
    },
  }),
  "watchings.removeDeck": defineMutation<{deckId: DeckId; userId: UserId}, void>({
    type: "delete",
    model: "deckSubscription",
    // the data names the deck, not the subscription row
    optimistic: (tx, {deckId, userId}) => {
      const accountId = tx.get("_root", "_root", "account");
      for (const sub of tx.find("account", accountId, "deckSubscriptions", {userId})) {
        if (sub.deck === deckId) tx.delete("deckSubscription", sub.id);
      }
    },
  }),

  "watchings.addCard": defineMutation<{cardId: CardId}, {id: CardSubscriptionId}, {userId: UserId}>(
    {
      type: "create",
      model: "cardSubscription",
    }
  ),
  "watchings.removeCard": defineMutation<{cardId: CardId}, void, {userId: UserId}>({
    type: "delete",
    model: "cardSubscription",
  }),

  "search.save": defineMutation<{forceOr: boolean; tokens: SearchFilter[]}, {id: SavedSearchId}>({
    type: "create",
    model: "savedSearch",
  }),
  "search.delete": defineMutation<{id: SavedSearchId}, void>({
    type: "delete",
    model: "savedSearch",
  }),

  "files.update": defineMutation<{id: FileId; size: number; meta: FileMeta}, void>({
    // action is only called by worker, no client directly
    type: "update",
    model: "file",
  }),

  "releases.create": {
    // action is only called by release process, no client directly
    type: "create",
    model: "release",
  },

  "milestones.create": defineMutation<
    {
      accountId: AccountId;
      name: string;
      description?: string;
      color: "gray" | "brown" | "yellow" | "red" | "pink" | "blue" | "green";
      date: string;
      startDate?: string | null;
      isGlobal: boolean;
      projectIds: (ProjectId | null)[];
    },
    {id: MilestoneId; accountSeq: number}
  >({
    type: "create",
    model: "milestone",
    convertDataForOptimistic: (data) => ({
      // convert "2016-11-23" to {year: 2016, month: 11, day: 23}
      ...data,
      ...(data.date ? {date: dateStrToDay(data.date)} : {}),
      ...(data.startDate ? {startDate: dateStrToDay(data.startDate)} : {}),
    }),
    implicit: () => [implCreate("milestoneProject")],
  }),
  "milestones.update": defineMutation<
    {
      id: MilestoneId;
      name?: string;
      description?: string;
      coverFileId?: FileId | null;
      color?: "gray" | "brown" | "yellow" | "red" | "pink" | "blue" | "green";
      date?: string;
      startDate?: string | null;
      handSyncEnabled?: boolean;
      isGlobal?: boolean;
      projectIds?: (ProjectId | null)[];
      manualOrderLabels?: (string | null)[];
      userCapacities?: {[userId: UserId]: number};
      preferredOrder?: CardOrder | null;
    },
    void
  >({
    type: "update",
    model: "milestone",
    convertDataForOptimistic: (data) => ({
      // convert "2016-11-23" to {year: 2016, month: 11, day: 23}
      ...data,
      ...(data.date ? {date: dateStrToDay(data.date)} : {}),
      ...(data.startDate ? {startDate: dateStrToDay(data.startDate)} : {}),
    }),
    implicit: ({handSyncEnabled}) => [
      implCreate("milestoneProject"),
      implUpdate("card", {milestoneId: INVALIDATE}),
      implUpdate("deck", {milestoneId: INVALIDATE}),
      ...(handSyncEnabled ? handUpdates : []),
    ],
  }),
  "milestones.renameZone": defineMutation<
    {id: MilestoneId} & ZoneRename,
    void,
    ZoneRenameSupport<CardId>
  >({
    type: "void",
    implicit: (data) => [
      implUpdate("milestone", {id: data.id, manualOrderLabels: renamedZoneLabels(data)}),
      ...cardZoneRenameUpdates("milestone", data),
    ],
  }),
  "milestones.delete": defineMutation<{id: MilestoneId}, void>({
    type: "delete",
    model: "milestone",
    implicit: ({id}) => [
      implUpdate("milestone", {id, isDeleted: true}),
      implCreate("cardHistory"),
      implUpdate("card", {milestoneId: INVALIDATE}),
      implUpdate("deck", {milestoneId: INVALIDATE}),
    ],
  }),
  "milestones.pin": defineMutation<{milestoneId: MilestoneId | null; userId: UserId}, void>({
    type: "void",
    implicit: ({milestoneId}) =>
      milestoneId
        ? [implCreate("pinnedMilestone"), implUpdate("pinnedMilestone", {milestoneId})]
        : [implDelete("pinnedMilestone")],
  }),
  // called by the server only
  "milestones.updateStats": {
    type: "void",
    implicit: ({id}: any) => [implUpdate("milestone", {id, stats: INVALIDATE})],
  },
  "milestoneProgress.update": defineMutation<{milestoneId: MilestoneId; date: string}, void>({
    type: "void",
    implicit: (data) => [
      implCreate("milestoneProgress", data),
      implUpdate("milestoneProgress", {...data, progress: INVALIDATE}),
    ],
  }),

  "sprints.createConfig": defineMutation<
    {
      name: string;
      runLabelTemplate?: string | null;
      color: "gray" | "brown" | "yellow" | "red" | "pink" | "blue" | "green";
      isGlobal: boolean;
      upcomingSprints: number;
      sprintDurationWeeks: number;
      sprintStartWeekday?: number;
      sprintStartDate?: string;
      projectIds: (ProjectId | null)[];
      moveOnFinish?: ("undone" | "review" | "blocked")[];
      endHour: number;
      endHourTimezone: string;
    },
    {id: SprintConfigId; sprints: {id: SprintId; accountSeq: number}[]}
  >({
    type: "create",
    model: "sprintConfig",
    implicit: () => [implCreate("sprintProject"), implCreate("sprint")],
  }),
  "sprints.updateConfig": defineMutation<
    {
      id: SprintConfigId;
      name?: string;
      runLabelTemplate?: string | null;
      color?: "gray" | "brown" | "yellow" | "red" | "pink" | "blue" | "green";
      isGlobal?: boolean;
      upcomingSprints?: number;
      sprintDurationWeeks?: number;
      sprintStartWeekday?: number;
      autoBeastModeDurationHours?: number | null;
      beastGracePeriodHours?: number;
      projectIds?: (ProjectId | null)[];
      stopOn?: string | null;
      moveOnFinish?: ("undone" | "review" | "blocked")[];
      preferredOrder?: CardOrder | null;
      endHour?: number;
      endHourTimezone?: string;
      autoAssignStartedCard?: boolean;
      autoAssignNewCard?: boolean;
    },
    void
  >({
    type: "update",
    model: "sprintConfig",
    convertDataForOptimistic: (data) => ({
      ...data,
      ...(data.stopOn ? {stopOn: dateStrToDay(data.stopOn)} : {}),
    }),
    implicit: () => [
      implCreate("sprintProject"),
      implCreate("sprint"),
      implUpdate("sprint", {
        startDate: INVALIDATE,
        endDate: INVALIDATE,
        isDeleted: INVALIDATE,
        lockedAt: INVALIDATE,
      }),
      implUpdate("card", {sprintId: INVALIDATE}),
      implUpdate("pinnedMilestone", {
        sprintId: INVALIDATE,
        milestoneId: null,
      }),
    ],
  }),
  "sprints.updateSprint": defineMutation<
    {
      id: SprintId;
      name?: string | null;
      description?: string;
      coverFileId?: FileId | null;
      handSyncEnabled?: boolean;
      manualOrderLabels?: (string | null)[];
      userCapacities?: {[userId: UserId]: number};
      autoMilestoneId?: MilestoneId | null;
      lockIn?: boolean;
      complete?: boolean;
    },
    void
  >({
    type: "update",
    model: "sprint",
    implicit: ({id, autoMilestoneId, lockIn}) => {
      const updates: ImplicitUpdate[] = [];
      if (autoMilestoneId) {
        updates.push(implUpdate("card", {milestoneId: INVALIDATE}));
      }
      if (lockIn) {
        updates.push(implUpdate("sprint", {id, lockedAt: new Date()}));
      }
      return updates;
    },
  }),
  "sprints.renameZone": defineMutation<
    {id: SprintId} & ZoneRename,
    void,
    ZoneRenameSupport<CardId>
  >({
    type: "void",
    implicit: (data) => [
      implUpdate("sprint", {id: data.id, manualOrderLabels: renamedZoneLabels(data)}),
      ...cardZoneRenameUpdates("sprint", data),
    ],
  }),
  "sprints.updateStats": {
    type: "void",
    implicit: ({id}: any) => [implUpdate("sprint", {id, stats: INVALIDATE})],
  },
  "sprintProgress.update": defineMutation<{sprintId: SprintId; date: string}, void>({
    type: "void",
    implicit: (data) => [
      implCreate("sprintProgress", data),
      implUpdate("sprintProgress", {...data, progress: INVALIDATE}),
    ],
  }),
  "sprintConfigProgress.update": defineMutation<
    {sprintConfigId: SprintConfigId; date: string},
    void
  >({
    type: "void",
    implicit: (data) => [
      implCreate("sprintConfigProgress", data),
      implUpdate("sprintConfigProgress", {...data, progress: INVALIDATE}),
    ],
  }),

  "hints.dismiss": defineMutation<{hintKey: string}, void>({
    type: "create",
    model: "userDismissedHint",
  }),
  "hints.undismiss": defineMutation<{hintKey: string}, void>({
    type: "delete",
    model: "userDismissedHint",
  }),

  "billing.addCreditCard": {
    type: "void",
  },
  "billing.createSubscription": {
    type: "void",
  },
  "billing.upgradeSubscription": {
    type: "void",
  },
  "billing.downgradeSubscription": {
    type: "void",
  },
  "billing.revokeSubscriptionDowngrade": {
    type: "void",
  },
  "billing.cancelSubscription": {
    type: "void",
  },
  "billing.revokeSubscriptionCancellation": {
    type: "void",
  },
  "billing.changeBillingInterval": {type: "void"},
  "billing.revokePendingIntervalChange": {type: "void"},
  "billing.updatePurchasedSeats": {
    type: "void",
  },
  "billing.serverUpdate": {
    type: "void",
    implicit: ({accountId}: any) => [
      implCreate("stripeAccountSync"),
      implUpdate("stripeAccountSync", {
        accountId,
        status: INVALIDATE,
        euVatIdData: INVALIDATE,
        vatCountryCode: INVALIDATE,
        vatTaxPercentage: INVALIDATE,
        centsPerSeat: INVALIDATE,
        billingCycleStart: INVALIDATE,
        billingCycleEnd: INVALIDATE,
        grossActualBalance: INVALIDATE,
        grossBonusBalance: INVALIDATE,
        netGiftBalance: INVALIDATE,
        planType: INVALIDATE,
        paymentMethod: INVALIDATE,
        hasBeenCancelledAt: INVALIDATE,
        pendingPlanType: INVALIDATE,
        planName: INVALIDATE,
        purchasedSeats: INVALIDATE,
        pendingChanges: INVALIDATE,
        repeatingCoupon: INVALIDATE,
      }),
      implUpdate("account", {id: accountId, offeringTrial: INVALIDATE}),
    ],
  },
  "billing.addInvoice": {type: "create", model: "invoice"},

  "githubIntegration.addHook": defineMutation<{id: IntegrationId; repo: GithubRepo}, void>({
    type: "update",
    model: "integration",
    implicit: ({id}) => [implUpdate("integration", {id, userData: INVALIDATE})],
  }),
  "githubIntegration.addHookViaRepoId": defineMutation<{id: IntegrationId; repoId: number}, void>({
    type: "update",
    model: "integration",
    implicit: ({id}) => [implUpdate("integration", {id, userData: INVALIDATE})],
  }),
  "githubIntegration.removeHook": defineMutation<{id: IntegrationId; hookId: number}, void>({
    type: "update",
    model: "integration",
    implicit: ({id}) => [implUpdate("integration", {id, userData: INVALIDATE})],
  }),
  "githubIntegration.removeIntegration": defineMutation<{id: IntegrationId}, void>({
    type: "delete",
    model: "integration",
  }),
  "scmWebhooks.create": defineMutation<
    {provider: ScmWebhookProvider},
    // the secret, returned exactly once — the provider's own settings hold it from here on
    {id: IntegrationId; link: string; secret: string},
    // puts the new row in `account.gitlabWebhooks` / `account.bitbucketWebhooks`
    {accountId: AccountId}
  >({
    type: "create",
    model: "integration",
  }),
  "scmWebhooks.remove": defineMutation<{id: IntegrationId; provider: ScmWebhookProvider}, void>({
    type: "delete",
    model: "integration",
  }),
  "slackIntegration.update": defineMutation<
    {id: IntegrationId; projectToChannelInfo: Record<ProjectId, SlackProjectChannelInfo>},
    void
  >({
    type: "update",
    model: "integration",
    implicit: ({id}) => [implUpdate("integration", {id, userData: INVALIDATE})],
  }),
  "slackIntegration.remove": defineMutation<{id: IntegrationId}, void>({
    type: "delete",
    model: "integration",
    implicit: () => [implDelete("integration")],
  }),
  "userReport.createSettings": defineMutation<
    {name: string; targetDeckId: DeckId},
    {id: UserReportSettingId; accessKey: string; userToken: string}
  >({
    type: "create",
    model: "userReportSetting",
    implicit: () => [implCreate("userReportToken")],
  }),
  "userReport.updateSettings": defineMutation<
    {
      id: UserReportSettingId;
      name: string;
      fileSizeBytesLimitInMb: number;
      deckMapping: UserReportDeckMapping;
      prioMapping: UserReportPrioMapping;
    },
    void
  >({
    type: "update",
    model: "userReportSetting",
  }),
  "userReport.regenerateSettingsToken": defineMutation<
    {id: UserReportSettingId},
    {accessKey: string}
  >({
    type: "void",
  }),
  "userReport.deleteSettings": defineMutation<{id: UserReportSettingId}, void>({
    type: "delete",
    model: "userReportSetting",
  }),
  "userReport.updateToken": defineMutation<{token: string; enabled: boolean}, void>({
    type: "update",
    model: "userReportToken",
  }),

  "discordIntegration.updateGuild": defineMutation<
    {
      id: DiscordGuildId;
      karmaRoleThresholds?: Record<string, number>;
      userMapping?: Record<string, string>;
    },
    void
  >({
    type: "update",
    model: "discordGuild",
  }),
  "discordIntegration.createProjectNotification": defineMutation<
    {
      guildId: DiscordGuildId;
      disabledTypes: Partial<Record<DiscordNotificationType, boolean>>;
      discordChannelId: string;
      projectId: ProjectId;
    },
    {id: DiscordProjectNotificationId}
  >({
    type: "create",
    model: "discordProjectNotification",
  }),
  "discordIntegration.updateProjectNotification": defineMutation<
    {
      id: DiscordProjectNotificationId;
      disabledTypes: Partial<Record<DiscordNotificationType, boolean>>;
      discordChannelId: string;
      projectId: ProjectId;
    },
    void
  >({
    type: "update",
    model: "discordProjectNotification",
  }),
  "discordIntegration.deleteProjectNotification": defineMutation<
    {id: DiscordProjectNotificationId},
    void
  >({
    type: "delete",
    model: "discordProjectNotification",
  }),
  "discordIntegration.createSlashCommand": defineMutation<
    CreateDiscordSlashCommandParams,
    {id: DiscordSlashCommandId}
  >({
    type: "create",
    model: "discordSlashCommand",
  }),
  "discordIntegration.updateSlashCommand": defineMutation<
    UpdateDiscordSlashCommandParams,
    {nextCommandId: DiscordSlashCommandId}
  >({
    type: "update",
    model: "discordSlashCommand",
  }),
  "discordIntegration.deleteSlashCommand": defineMutation<{id: DiscordSlashCommandId}, void>({
    type: "delete",
    model: "discordSlashCommand",
  }),
  "discordIntegration.removeGuild": defineMutation<{id: DiscordGuildId}, void>({
    type: "delete",
    model: "discordGuild",
  }),
  "discordIntegration.recheckChannelProblems": defineMutation<
    {guildId: DiscordGuildId; channelId: string},
    | {botRemoved: true}
    | {botRemoved: false; remaining: {action: DiscordChannelPermissionAction; missing: string[]}[]}
  >({
    type: "void",
    implicit: ({guildId}) => [
      implUpdate("discordGuild", {
        id: guildId,
        channelProblems: INVALIDATE,
        botRemovedAt: INVALIDATE,
      }),
    ],
  }),
  "discordIntegration.setChannelProblemsDismissed": defineMutation<
    {guildId: DiscordGuildId; channelId: string; dismissed: boolean},
    void
  >({
    type: "void",
    implicit: ({guildId}) => [
      implUpdate("discordGuild", {id: guildId, channelProblems: INVALIDATE}),
    ],
  }),

  "jiraIntegration.createProjectSync": defineMutation<
    {siteId: JiraSiteId; label: string; jiraProjectId: string; syncOptions: JiraSyncOptions},
    {id: JiraProjectSyncId}
  >({
    type: "create",
    model: "jiraProjectSync",
  }),
  "jiraIntegration.updateProjectSync": defineMutation<
    {id: JiraProjectSyncId; syncOptions: JiraSyncOptions},
    void
  >({
    type: "update",
    model: "jiraProjectSync",
  }),
  "jiraIntegration.deleteProjectSync": defineMutation<{id: JiraProjectSyncId}, void>({
    type: "delete",
    model: "jiraProjectSync",
  }),

  "projectSelections.set": defineMutation<
    {userId: UserId; projectIds: ProjectId[]},
    {projectSelections: {id: ProjectSelectionId; projectId: ProjectId}[]}
  >({
    type: "create",
    model: "projectSelection",
    // the given projects replace the user's selection in the current organization
    optimistic: (tx, {userId, projectIds}) => {
      const accountId = tx.get("_root", "_root", "account");
      // Create first: once the server answered, the reloaded list holds the saved rows and
      // `create` returns their ids, so they must not be deleted as "the old selection".
      const newIds = new Set(
        projectIds.map((projectId) =>
          tx.create(
            "projectSelection",
            {project: projectId, user: userId, account: accountId},
            {key: projectId}
          )
        )
      );
      for (const {id} of tx.find("user", userId, "projectSelections", {accountId})) {
        if (!newIds.has(id)) tx.delete("projectSelection", id);
      }
    },
    createdIds: ({projectSelections}) => ({
      projectSelection: Object.fromEntries(projectSelections.map((ps) => [ps.projectId, ps.id])),
    }),
  }),

  "publicProjectMembership.create": defineMutation<
    {projectId: ProjectId; digestFrequencyInDays: number | null},
    void,
    {userId: UserId}
  >({
    type: "create",
    model: "publicProjectMembership",
  }),
  "publicProjectMembership.update": defineMutation<
    {projectId: ProjectId; digestFrequencyInDays: number | null},
    void,
    {userId: UserId}
  >({
    type: "update",
    model: "publicProjectMembership",
  }),
  "publicProjectMembership.delete": defineMutation<{projectId: ProjectId}, void, {userId: UserId}>({
    type: "delete",
    model: "publicProjectMembership",
  }),

  "affiliateCodes.create": defineMutation<
    {accountId: AccountId; label: string; message: string},
    {id: AffiliateCodeId},
    {creatorId: UserId}
  >({
    type: "create",
    model: "affiliateCode",
  }),
  "affiliateCodes.update": defineMutation<
    {id: AffiliateCodeId; label: string; message: string},
    void
  >({
    type: "update",
    model: "affiliateCode",
  }),
  "affiliateCodes.delete": defineMutation<{id: AffiliateCodeId}, void>({
    type: "update",
    model: "affiliateCode",
    implicit: ({id}) => [implUpdate("affiliateCode", {isDeleted: true, id})],
  }),

  "timeTracking.start": defineMutation<
    {userId: UserId; cardId: CardId},
    {id: TimeTrackingSegmentId}
  >({
    type: "create",
    model: "timeTrackingSegment",
    implicit: ({cardId, userId}) => [
      implCreate("activeTimeTracker", {cardId, userId}),
      implUpdate("activeTimeTracker", {cardId, userId}),
      // might stop existing one
      implUpdate("timeTrackingSegment", {finishedAt: INVALIDATE}),
      implDelete("timeTrackingSegment"),
      implUpdate("timeTrackingSum", {
        sumMs: INVALIDATE,
        runningModifyDurationMsBy: INVALIDATE,
        runningStartedAt: INVALIDATE,
      }),
      implDelete("timeTrackingSum"),
      implUpdate("card", {status: INVALIDATE}),
    ],
  }),
  "timeTracking.stop": defineMutation<{id: TimeTrackingSegmentId}, void>({
    type: "update",
    model: "timeTrackingSegment",
    implicit: ({id}) => [
      implUpdate("timeTrackingSegment", {finishedAt: INVALIDATE, id}),
      implDelete("timeTrackingSegment", {id}),
      implUpdate("card", {status: INVALIDATE, derivedStatus: INVALIDATE}),
      implUpdate("timeTrackingSum", {
        sumMs: INVALIDATE,
        runningModifyDurationMsBy: INVALIDATE,
        runningStartedAt: INVALIDATE,
      }),
      implDelete("timeTrackingSum"),
    ],
  }),
  "timeTracking.setModifyDuration": defineMutation<
    {id: TimeTrackingSegmentId; modifyDurationMsBy: number},
    void
  >({
    type: "update",
    model: "timeTrackingSegment",
    implicit: ({id}) => [
      implUpdate("timeTrackingSum", {
        sumMs: INVALIDATE,
        runningModifyDurationMsBy: INVALIDATE,
      }),
      implUpdate("timeTrackingSegment", {id, autoFinishedState: INVALIDATE}),
    ],
  }),
  "timeTracking.create": defineMutation<
    {startedAt: Date; userId: UserId; cardId: CardId; durationInMs: number},
    {id: TimeTrackingSegmentId}
  >({
    type: "create",
    model: "timeTrackingSegment",
    implicit: ({cardId, userId}) => [
      implCreate("timeTrackingSum"),
      implUpdate("timeTrackingSum", {userId, cardId, sumMs: INVALIDATE}),
      implUpdate("timeTrackingSum", {userId: null, cardId, sumMs: INVALIDATE}),
    ],
  }),
  "timeTracking.delete": defineMutation<{id: TimeTrackingSegmentId}, void>({
    type: "delete",
    model: "timeTrackingSegment",
    implicit: () => [implUpdate("timeTrackingSum", {sumMs: INVALIDATE})],
  }),
  "timeTracking.close": defineMutation<{id: ActiveTimeTrackerId}, void>({
    type: "delete",
    model: "activeTimeTracker",
  }),

  "votes.vote": defineMutation<{cardId: CardId}, {id: CardUpvoteId}>({
    type: "create",
    model: "cardUpvote",
    implicit: ({cardId}) => [implUpdate("card", {id: cardId, meta: INVALIDATE})],
  }),
  "votes.unvote": defineMutation<{id: CardUpvoteId}, void, {cardId: CardId}>({
    type: "delete",
    model: "cardUpvote",
    implicit: ({cardId}) => [implUpdate("card", {id: cardId, meta: INVALIDATE})],
  }),
  "cardOrders.addAfter": defineMutation<
    {
      targetId: CardId | null;
      cardIds: CardId[];
      context: "milestone" | "deck" | "sprint";
      label: string | null;
    },
    void
  >({
    type: "void",
    optimistic: addMissingCardOrders,
    implicit: ({cardIds, context: sortCtx, label}) => [
      implCreate("cardOrder"),
      implCreate("activity", {cardId: INVALIDATE}),
      ...cardIds.map((cardId) =>
        implUpdate("cardOrder", {cardId, context: sortCtx, sortValue: INVALIDATE, label})
      ),
    ],
  }),
  "cardOrders.addBefore": defineMutation<
    {
      targetId: CardId | null;
      cardIds: CardId[];
      context: "milestone" | "deck" | "sprint";
      label: string | null;
    },
    void
  >({
    type: "void",
    optimistic: addMissingCardOrders,
    implicit: ({cardIds, context: sortCtx, label}) => [
      implCreate("cardOrder"),
      implCreate("activity", {cardId: INVALIDATE}),
      ...cardIds.map((cardId) =>
        implUpdate("cardOrder", {cardId, context: sortCtx, sortValue: INVALIDATE, label})
      ),
    ],
  }),
  "cardOrders.updateLabel": defineMutation<
    {
      cardIds: CardId[];
      context: "milestone" | "deck" | "sprint";
      label: string | null;
    },
    void
  >({
    type: "void",
    implicit: ({cardIds, context: sortCtx, label}) =>
      cardIds.map((cardId) => implUpdate("cardOrder", {cardId, context: sortCtx, label})),
  }),
  "lastSeenUpvotes.justSeen": defineMutation<{accountId: AccountId}, void, {userId: UserId}>({
    type: "create",
    model: "lastSeenCardUpvote",
    implicit: ({userId, accountId}) => [
      implUpdate("lastSeenCardUpvote", {userId, accountId, lastSeenAt: INVALIDATE}),
    ],
  }),

  "visionBoard.createBoard": defineMutation<
    {cardId: CardId},
    {id: VisionBoardId; accountSeq: number}
  >({
    type: "create",
    model: "visionBoard",
    implicit: ({cardId}, opts) => [
      implUpdate("card", {cardId, visionBoardId: opts._isOptimistic ? INVALIDATE : opts.id}),
    ],
  }),
  "visionBoard.deleteBoard": defineMutation<{id: VisionBoardId}, void>({
    type: "void",
    implicit: ({id}) => [
      implUpdate("visionBoard", {id, state: "deleted"}),
      implUpdate("card", {visionBoard: INVALIDATE}),
    ],
  }),
  "visionBoard.updateBoard": defineMutation<
    {id: VisionBoardId; state: "default" | "read_only" | "locked"},
    void
  >({
    type: "update",
    model: "visionBoard",
  }),
  "visionBoard.createQuery": defineMutation<
    {
      cardId: CardId;
      query: VisionBoardSearchQuery;
      prevQueryId?: VisionBoardQueryId | null;
    },
    {id: VisionBoardQueryId}
  >({
    type: "create",
    model: "visionBoardQuery",
  }),
  "visionBoard.duplicateQueries": defineMutation<
    {ids: VisionBoardQueryId[]},
    {oldIdToNewId: Record<VisionBoardQueryId, VisionBoardQueryId>}
  >({
    type: "create",
    model: "visionBoardQuery",
  }),
  "visionBoard.markQueryStale": defineMutation<{id: VisionBoardQueryId}, void>({
    type: "void",
    implicit: ({id}) => [implUpdate("visionBoardQuery", {id, isStale: true, payload: null})],
  }),
  "visionBoard.reloadQuery": defineMutation<
    {id: VisionBoardQueryId; type: "aggr" | "list"},
    {payload: VisionBoardQueryPayload; type: "aggr" | "list"}
  >({
    type: "void",
    implicit: ({id, type}, opts) => [
      implUpdate("visionBoardQuery", {
        id,
        isStale: false,
        payload: opts._isOptimistic ? INVALIDATE : opts.payload,
        type,
      }),
    ],
  }),

  "accountApiTokens.create": defineMutation<
    {
      label: string;
      // one tier, and any add-on roles
      roles: TokenRoleName[];
      // API-only; the modal sends it empty
      removeScopes: string[];
      projectIds: ProjectId[];
      expiresAt?: string | null;
    },
    // the plaintext token, returned exactly once — nothing else ever exposes it
    {id: ApiTokenId; token: string},
    // puts the optimistic row in `account.accountApiTokens`
    {accountId: AccountId}
  >({
    type: "create",
    model: "apiToken",
  }),
  "accountApiTokens.update": defineMutation<
    {
      id: ApiTokenId;
      label: string;
      roles: TokenRoleName[];
      removeScopes: string[];
      projectIds: ProjectId[];
      expiresAt?: string | null;
    },
    void,
    // the token's principal: the project set lives on its `project_users` rows, not the token row
    {userId: UserId}
  >({
    type: "update",
    model: "apiToken",
    implicit: ({userId}) => [implUpdate("user", {id: userId, explicitProjectAccess: INVALIDATE})],
  }),
  "accountApiTokens.revoke": defineMutation<{id: ApiTokenId}, void>({
    type: "delete",
    model: "apiToken",
  }),

  "userApiTokens.create": defineMutation<
    {
      label: string;
      roles: TokenRoleName[];
      removeScopes: string[];
      expiresAt?: string | null;
    },
    {id: ApiTokenId; token: string},
    // puts the optimistic row in both lists
    {accountId: AccountId}
  >({
    type: "create",
    model: "apiToken",
  }),
  "userApiTokens.update": defineMutation<
    {
      id: ApiTokenId;
      label: string;
      roles: TokenRoleName[];
      removeScopes: string[];
      expiresAt?: string | null;
    },
    void
  >({
    type: "update",
    model: "apiToken",
  }),
  "userApiTokens.revoke": defineMutation<{id: ApiTokenId}, void>({
    type: "delete",
    model: "apiToken",
  }),
  // an admin ending someone else's token; the owner is emailed by the server
  "userApiTokens.revokeAsAdmin": defineMutation<{id: ApiTokenId}, void>({
    type: "delete",
    model: "apiToken",
  }),
  "userApiTokens.setEnabledForAccount": defineMutation<
    {enabled: boolean},
    void,
    {accountId: AccountId}
  >({
    // `void` plus an explicit implicit: mate can't read `accountId` as the entry's id, so a plain
    // `update` would invalidate nothing
    type: "void",
    implicit: ({accountId, enabled}) => [
      // every personal token in the org changes standing at once
      implUpdate("account", {
        id: accountId,
        userApiTokensEnabled: enabled,
        userApiTokens: INVALIDATE,
      }),
    ],
  }),

  // ends the grant in every org it covers
  "oauthGrants.disconnect": defineMutation<{id: OauthGrantId}, void>({
    type: "delete",
    model: "oauthGrant",
  }),
  // takes the request's org out of a member's grant; the server mails the member
  "oauthGrants.revokeAsAdmin": defineMutation<
    {grantId: OauthGrantId},
    void,
    {accountId: AccountId}
  >({
    type: "void",
    implicit: ({grantId, accountId}) => [
      implDelete("oauthGrantAccount", {grantId, accountId}),
      // the grant is gone too when this was its last org
      implUpdate("oauthGrant", {id: grantId, grantAccounts: INVALIDATE}),
    ],
  }),
  "mcp.setEnabledForAccount": defineMutation<{enabled: boolean}, void, {accountId: AccountId}>({
    type: "void",
    implicit: ({accountId, enabled}) => [
      implUpdate("account", {id: accountId, mcpEnabled: enabled}),
    ],
  }),

  "forecastOverwrites.create": defineMutation<
    {projectId: ProjectId; name: string; payload: ForecastOverwritePayload},
    {id: ForecastOverwriteId}
  >({
    type: "create",
    model: "forecastOverwrite",
  }),
  "forecastOverwrites.update": defineMutation<
    {id: ForecastOverwriteId; name?: string; payload?: ForecastOverwritePayload},
    {id: ForecastOverwriteId}
  >({
    type: "update",
    model: "forecastOverwrite",
  }),
  "forecastOverwrites.remove": defineMutation<{id: ForecastOverwriteId}, {id: ForecastOverwriteId}>(
    {
      type: "delete",
      model: "forecastOverwrite",
    }
  ),

  "onboarding.progress": {
    type: "void",
  },
  "wizards.progress": defineMutation<{id: WizardId; currentStep?: string; data?: unknown}, void>({
    type: "update",
    model: "wizard",
  }),
  "wizards.skip": defineMutation<{id: WizardId}, void>({
    type: "void",
    implicit: ({id}) => [implUpdate("wizard", {id, finishedAt: INVALIDATE}), implCreate("wizard")],
  }),
  "wizards.finish": defineMutation<{id: WizardId}, void>({
    type: "void",
    implicit: ({id}) => [implUpdate("wizard", {id, finishedAt: INVALIDATE}), implCreate("wizard")],
  }),
} satisfies Partial<Record<ActionKey | ServerPushKey, MutationDef<any>>>;

// The collector asks this outside production and throws on a non-null answer.
const getMissingReadScope = (modelName: string, group: ScopeGroup): Scope | null => {
  const held = peekAccountScopes();
  const scope = modelReadScope(modelName, group);
  return held && !held.has(scope) ? scope : null;
};

// `typeof mutations` has to sit inside a type literal, which TypeScript resolves lazily. Named
// eagerly (as a type argument of `createApi`) it loops: the search categories read `api`, and a
// mutation's params type is built from those categories.
type Api = Omit<ReturnType<typeof createApi<Record<string, MutationDef>>>, "mutate"> & {
  mutate: Mutate<typeof mutations>;
};

export const api: Api = createApi({
  descriptions,
  fetcher,
  mutations,
  dispatcher,
  getMissingReadScope,
  onCacheClear: forgetAccountScopes,
  fieldDependents,
});
